import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Package, Search, RefreshCw, MapPin, AlertTriangle, TrendingDown,
  Plus, Edit2, ChevronDown, ChevronUp, ExternalLink, Image as ImageIcon,
  Building2, DollarSign, Boxes, Filter, Download, Upload, PlusCircle, MinusCircle,
  CloudUpload, Loader2, Check, X, MoreVertical, History, ChevronsUpDown, Sparkles, Printer, Equal
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '@/context/SettingsContext';
import { useAuth } from '@/context/AuthContext';
import usePermissions from '@/hooks/usePermissions';
import ImageLightbox from '@/components/ImageLightbox';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Safe API error → string (422 detail can be an array of objects; passing it to toast crashes React)
const apiErr = (error) => {
  const d = error?.response?.data?.detail;
  if (!d) return null;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join('; ');
  return JSON.stringify(d);
};

// Location Inventory Row Component for Shopify-style inventory management
const LocationInventoryRow = ({ location, isSelected, onSelect, onSetLevel, disabled }) => {
  const [editMode, setEditMode] = useState(false);
  const [editValue, setEditValue] = useState(location.available?.toString() || '0');
  
  const handleSave = () => {
    const newLevel = parseInt(editValue);
    if (!isNaN(newLevel) && newLevel >= 0) {
      onSetLevel(newLevel);
      setEditMode(false);
    }
  };
  
  const handleCancel = () => {
    setEditValue(location.available?.toString() || '0');
    setEditMode(false);
  };
  
  return (
    <div 
      className={`grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-wt-border hover:bg-wt-subtle/50 cursor-pointer ${isSelected ? 'bg-wt-gold/5' : ''}`}
      onClick={onSelect}
    >
      <div className="col-span-5 flex items-center gap-2">
        <MapPin className={`h-4 w-4 ${location.active ? 'text-green-400' : 'text-wt-text-muted'}`} />
        <div>
          <p className="text-sm font-medium text-wt-text-primary">{location.location_name}</p>
          {location.city && (
            <p className="text-xs text-wt-text-muted">{location.city}</p>
          )}
        </div>
      </div>
      <div className="col-span-2 text-center">
        <span className={`font-semibold ${location.available > 0 ? 'text-green-400' : 'text-red-400'}`}>
          {location.available || 0}
        </span>
      </div>
      <div className="col-span-2 text-center text-wt-text-primary">
        {location.on_hand || 0}
      </div>
      <div className="col-span-3 flex justify-center gap-1" onClick={(e) => e.stopPropagation()}>
        {editMode ? (
          <>
            <Input
              type="number"
              min="0"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-16 h-8 text-sm bg-wt-bg border-wt-border text-wt-text-primary text-center"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') handleCancel();
              }}
            />
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={handleSave}
              disabled={disabled}
              className="h-8 w-8 p-0 text-green-400 hover:text-green-300"
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={handleCancel}
              className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditMode(true)}
            disabled={disabled}
            className="h-8 text-xs border-wt-border text-wt-text-secondary hover:text-wt-text-primary"
          >
            <Edit2 className="h-3 w-3 mr-1" />
            Edit
          </Button>
        )}
      </div>
    </div>
  );
};

const InventoryMasterPage = () => {
  const { formatCurrency } = useSettings();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canViewAnalytics = hasPermission('dashboard.analytics');
  const [items, setItems] = useState([]);
  const [labelDialog, setLabelDialog] = useState({ open: false, items: [], loading: false });

  const openLabelDialog = (labelItems) => {
    setLabelDialog({
      open: true,
      loading: false,
      items: labelItems.map(it => ({
        sku: it.sku,
        name: it.product_title || it.title || it.name || it.sku,
        qty: 1,
      })),
    });
  };

  const setLabelQty = (sku, qty) => setLabelDialog(p => ({
    ...p, items: p.items.map(it => it.sku === sku ? { ...it, qty } : it),
  }));

  const printLabels = async () => {
    const toPrint = labelDialog.items.filter(it => parseInt(it.qty) > 0);
    if (!toPrint.length) { toast.error('Set at least one quantity'); return; }
    setLabelDialog(p => ({ ...p, loading: true }));
    try {
      const res = await axios.post(`${API_BASE_URL}/labels/generate`,
        { items: toPrint.map(it => ({ sku: it.sku, qty: parseInt(it.qty) })) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      setLabelDialog({ open: false, items: [], loading: false });
    } catch (e) {
      toast.error('Failed to generate labels');
      setLabelDialog(p => ({ ...p, loading: false }));
    }
  };
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [expandedItems, setExpandedItems] = useState({});
  const [summary, setSummary] = useState({ total_items: 0, total_value: 0, low_stock_count: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total_pages: 1, has_more: false });
  
  // Zoho sync states
  const [zohoSyncStatus, setZohoSyncStatus] = useState(null);
  const [syncingToZoho, setSyncingToZoho] = useState(false);
  const [zohoConnected, setZohoConnected] = useState(false);
  const [shopifyConnected, setShopifyConnected] = useState(false);
  const [coverageChannels, setCoverageChannels] = useState([]);
  const [channelCoverage, setChannelCoverage] = useState({});
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [vendorMappingDialogOpen, setVendorMappingDialogOpen] = useState(false);
  const [adjustStockDialogOpen, setAdjustStockDialogOpen] = useState(false);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Vendor mapping form state
  const [vendorMappingForm, setVendorMappingForm] = useState({
    vendor_id: '',
    vendor_sku: '',
    cost_price: '',
    moq: 1,
    lead_time_days: 7
  });
  const [savingMapping, setSavingMapping] = useState(false);
  
  // Adjust stock form
  const [adjustmentType, setAdjustmentType] = useState('add');
  const [adjustmentQuantity, setAdjustmentQuantity] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [locationInventory, setLocationInventory] = useState([]);
  const [loadingLocationInventory, setLoadingLocationInventory] = useState(false);
  const [adjustingStock, setAdjustingStock] = useState(false);
  
  // Add item form
  const [newItemForm, setNewItemForm] = useState({
    sku: '',
    product_title: '',
    variant_title: '',
    total_available: 0,
    unit_cost: 0,
    min_stock_level: 5
  });
  
  // Adjustment history dialog
  const [adjustmentHistoryDialogOpen, setAdjustmentHistoryDialogOpen] = useState(false);
  const [adjustmentHistoryItem, setAdjustmentHistoryItem] = useState(null);
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Cost-change history dialog (May 2026) — separate from stock history.
  // Powered by GET /enterprise/inventory/{sku}/cost-history. Lets the
  // operator see every cost mutation (PO process, manual edit, backfill)
  // with vendor + PO context.
  const [costHistoryDialogOpen, setCostHistoryDialogOpen] = useState(false);
  const [costHistoryItem, setCostHistoryItem] = useState(null);
  const [costHistoryData, setCostHistoryData] = useState(null);
  const [loadingCostHistory, setLoadingCostHistory] = useState(false);

  // "Cost changed recently" indicator — feature B from the May 28 ask.
  // One bulk GET on page load gives us a Set<sku> of SKUs with a cost
  // change in the last 30 days so we can render a yellow ₹↑↓ chip on
  // the row without per-row API calls.
  const [recentCostChangeSkus, setRecentCostChangeSkus] = useState({});
  
  // Rack assignment
  const [racks, setRacks] = useState([]);
  const [selectedRackNumber, setSelectedRackNumber] = useState('');
  
  // Rack inventory popup
  const [rackInventoryDialogOpen, setRackInventoryDialogOpen] = useState(false);
  const [rackInventoryData, setRackInventoryData] = useState(null);
  const [loadingRackInventory, setLoadingRackInventory] = useState(false);
  const [rackInventoryLocation, setRackInventoryLocation] = useState(null);

  // Blocked-orders drill-down (which open orders are reserving this SKU)
  const [blockedOrdersDialogOpen, setBlockedOrdersDialogOpen] = useState(false);
  const [blockedOrdersData, setBlockedOrdersData] = useState(null);
  const [blockedOrdersSku, setBlockedOrdersSku] = useState('');
  const [loadingBlockedOrders, setLoadingBlockedOrders] = useState(false);
  
  // Smart rack list for adjust stock (filtered based on add/remove)
  const [availableRacksForAdjustment, setAvailableRacksForAdjustment] = useState([]);
  
  // Edit item form state
  const [editForm, setEditForm] = useState({
    unit_cost: 0,
    min_stock_level: 0,
    reorder_point: 0,
    reorder_quantity: 0,
    made_to_order: false,
    production_days: 0,
    processing_days: 0,
    express_eligible: false,
    express_sla_hours: 0,
    sell_by: 'company',
    marketplace_vendor_id: null,
    commission_pct_override: null,
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [syncToShopify, setSyncToShopify] = useState(true); // Default to sync
  const [availablePackages, setAvailablePackages] = useState([]);
  const [packageSearchOpen, setPackageSearchOpen] = useState(false);

  // Approved marketplace vendors (for Sell-By picker in edit modal)
  const [marketplaceVendors, setMarketplaceVendors] = useState([]);

  // Bulk selection + bulk marketplace-assign modal (Feb 2026 ask).
  // Lives on the table itself: tick rows, then open the bulk modal to
  // assign vendor / unassign / change sell_by for many SKUs in one shot.
  const [selectedInventoryIds, setSelectedInventoryIds] = useState([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignForm, setBulkAssignForm] = useState({
    sell_by: 'vendor',                 // 'vendor' | 'company'
    marketplace_vendor_id: '',
    commission_pct_override: '',
  });
  const [bulkAssigning, setBulkAssigning] = useState(false);
  
  // Vendors for mapping
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    loadInventory();
    loadLocations();
    loadVendors();
    loadZohoSyncStatus();
    loadRacks();
    loadIntegrationStatus();
    loadPackages();
    loadRecentCostChanges();
    // Marketplace vendors power both the bulk-assign modal AND the
    // per-row chip showing which vendor a SKU belongs to.
    loadMarketplaceVendors();
  }, []);

  const loadPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/couriers/packages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailablePackages(response.data.packages || []);
    } catch (error) {
      console.error('Failed to load packages:', error);
    }
  };

  // Debounced search - auto-search when user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.length >= 2 || searchTerm.length === 0) {
        loadInventory(1, false);
      }
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const loadInventory = async (page = 1, append = false) => {
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', '50');
      
      if (searchTerm) {
        params.append('search', searchTerm);
        // Include discontinued products when searching to ensure we find all matching items
        params.append('include_discontinued', 'true');
      }
      if (locationFilter !== 'all') params.append('location_id', locationFilter);
      if (stockFilter === 'low') params.append('low_stock', 'true');
      if (stockFilter === 'out') params.append('out_of_stock', 'true');
      if (stockFilter === 'in') params.append('stock_status', 'in_stock');
      
      const response = await axios.get(`${API_BASE_URL}/v1/inventory?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (append) {
        setItems(prev => [...prev, ...(response.data.items || [])]);
      } else {
        setItems(response.data.items || []);
      }
      loadChannelCoverage((response.data.items || []).map(i => i.sku).filter(Boolean), append);
      setSummary(response.data.summary || { total_items: 0, total_value: 0, low_stock_count: 0 });
      setPagination(response.data.pagination || { page: 1, limit: 50, total_pages: 1, has_more: false });
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreItems = () => {
    if (pagination.has_more && !loadingMore) {
      loadInventory(pagination.page + 1, true);
    }
  };

  const loadChannelCoverage = async (skus, append = false) => {
    if (!skus.length) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/channel-listings/coverage`, { skus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoverageChannels(response.data.channels || []);
      setChannelCoverage(prev => append ? { ...prev, ...(response.data.coverage || {}) } : (response.data.coverage || {}));
    } catch (error) {
      console.error('Failed to load channel coverage');
    }
  };

  const loadLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Failed to load locations');
    }
  };

  const loadVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Failed to load vendors');
    }
  };

  const loadZohoSyncStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/inventory/zoho-sync-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZohoSyncStatus(response.data);
    } catch (error) {
      console.error('Failed to load Zoho sync status');
    }
  };

  const loadIntegrationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/integrations/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZohoConnected(response.data?.zoho?.connected || false);
      setShopifyConnected(response.data?.shopify?.connected || false);
    } catch (error) {
      console.error('Failed to load integration status');
    }
  };

  const loadRacks = async (locationId = null) => {
    try {
      const token = localStorage.getItem('token');
      const params = locationId ? `?location_id=${locationId}` : '';
      const response = await axios.get(`${API_BASE_URL}/enterprise/warehouse/racks${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRacks(response.data.racks || response.data || []);
    } catch (error) {
      console.error('Failed to load racks');
      setRacks([]);
    }
  };

  const loadRackInventory = async (sku, locationId = null, locationName = null) => {
    setLoadingRackInventory(true);
    try {
      const token = localStorage.getItem('token');
      const params = locationId ? `?location_id=${locationId}` : '';
      const response = await axios.get(`${API_BASE_URL}/v1/inventory/${encodeURIComponent(sku)}/rack-inventory${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRackInventoryData(response.data);
      setRackInventoryLocation({ id: locationId, name: locationName });
      setRackInventoryDialogOpen(true);
    } catch (error) {
      console.error('Failed to load rack inventory:', error);
      toast.error('Failed to load rack inventory');
    } finally {
      setLoadingRackInventory(false);
    }
  };

  const loadBlockedOrders = async (sku, locationId = null) => {
    setBlockedOrdersSku(sku);
    setLoadingBlockedOrders(true);
    setBlockedOrdersDialogOpen(true);
    try {
      const token = localStorage.getItem('token');
      const params = locationId ? `?location_id=${locationId}` : '';
      const response = await axios.get(
        `${API_BASE_URL}/enterprise/inventory/${encodeURIComponent(sku)}/blocked-orders${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBlockedOrdersData(response.data);
    } catch (error) {
      console.error('Failed to load blocked orders:', error);
      toast.error('Failed to load blocked orders');
      setBlockedOrdersData(null);
    } finally {
      setLoadingBlockedOrders(false);
    }
  };

  const loadRacksForAdjustment = async (sku, locationId, adjustType) => {
    try {
      const token = localStorage.getItem('token');
      const params = locationId ? `?location_id=${locationId}` : '';
      const response = await axios.get(`${API_BASE_URL}/v1/inventory/${encodeURIComponent(sku)}/rack-inventory${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (adjustType === 'remove') {
        // For remove: only show racks with inventory
        setAvailableRacksForAdjustment(response.data.rack_inventory || []);
      } else {
        // For add: show all racks
        setAvailableRacksForAdjustment(response.data.all_racks || []);
      }
    } catch (error) {
      console.error('Failed to load racks for adjustment');
      setAvailableRacksForAdjustment([]);
    }
  };

  const handleSyncToZoho = async (syncAll = false) => {
    setSyncingToZoho(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/v1/inventory/sync-to-zoho?sync_all=${syncAll ? 'true' : 'false'}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      toast.success(`Synced ${result.synced} items to Zoho. ${result.failed} failed.`);
      loadInventory();
      loadZohoSyncStatus();
    } catch (error) {
      const detail = apiErr(error) || 'Failed to sync to Zoho';
      toast.error(detail, {
        duration: 15000,
        style: { whiteSpace: 'pre-line', maxWidth: '480px' },
      });
      console.error('[Zoho inventory sync] failed:', detail);
    } finally {
      setSyncingToZoho(false);
    }
  };

  const handleSyncSingleItemToZoho = async (itemId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/enterprise/inventory/${itemId}/sync-to-zoho`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      if (result.status === 'error') {
        toast.error(`Sync failed: ${result.error}`);
      } else {
        toast.success(`Item ${result.status} in Zoho`);
        loadInventory();
        loadZohoSyncStatus();
      }
    } catch (error) {
      toast.error(apiErr(error) || 'Failed to sync item');
    }
  };

  const handleSyncFromShopify = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/v1/inventory/sync-from-shopify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      loadInventory();
      loadLocations();
    } catch (error) {
      toast.error(apiErr(error) || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/enterprise/locations/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      loadLocations();
    } catch (error) {
      toast.error('Failed to sync locations');
    }
  };

  const toggleItemExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const openAdjustStockDialog = async (item) => {
    setSelectedItem(item);
    setAdjustmentType('add');
    setAdjustmentQuantity('');
    setAdjustmentReason('');
    setSelectedLocationId('');
    setSelectedRackNumber('');
    setAvailableRacksForAdjustment([]);
    setLocationInventory([]);
    setAdjustStockDialogOpen(true);
    
    // If item has shopify_inventory_item_id, fetch location-wise inventory
    if (item.shopify_inventory_item_id) {
      setLoadingLocationInventory(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE_URL}/shopify/inventory/${item.shopify_inventory_item_id}/levels`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setLocationInventory(response.data.locations || []);
        // Auto-select first active location
        const firstActive = response.data.locations?.find(l => l.active);
        if (firstActive) {
          setSelectedLocationId(firstActive.location_id);
        }
      } catch (error) {
        console.error('Failed to load location inventory:', error);
        // Don't show error - will fall back to simple mode
      } finally {
        setLoadingLocationInventory(false);
      }
    }
  };

  const handleAdjustStock = async () => {
    const quantity = parseInt(adjustmentQuantity);
    if (adjustmentType === 'set') {
      if (isNaN(quantity) || quantity < 0) {
        toast.error('Please enter a valid exact total (0 or more)');
        return;
      }
    } else if (isNaN(quantity) || quantity <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    setAdjustingStock(true);
    try {
      const token = localStorage.getItem('token');

      // Absolute correction: writes the exact total on the app master
      // (clears ghost stock) and syncs the corrected value outward.
      if (adjustmentType === 'set') {
        const response = await axios.post(
          `${API_BASE_URL}/v1/inventory/${selectedItem.id}/set-total`,
          {
            new_total: quantity,
            reason: adjustmentReason || 'Manual correction (set exact total)',
            location_id: selectedLocationId || null,
            sync_to_shopify: true
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'X-User-Id': user?.id || 'unknown',
              'X-User-Name': user?.full_name || user?.email || 'Unknown'
            }
          }
        );
        const syncStatus = response.data?.shopify_synced ? ' (Synced to Shopify)' : '';
        toast.success(`Stock corrected: ${response.data.previous_quantity} → ${response.data.new_quantity}${syncStatus}`);
        setAdjustStockDialogOpen(false);
        setSelectedRackNumber('');
        loadInventory();
        setAdjustingStock(false);
        return;
      }

      const quantityChange = adjustmentType === 'add' ? quantity : -quantity;
      // "default" means no specific rack (for old inventory without rack assignment)
      const rackNum = selectedRackNumber && selectedRackNumber !== 'default' ? selectedRackNumber : null;
      
      // If we have location inventory and a selected location, use the Shopify API directly
      if (locationInventory.length > 0 && selectedLocationId && selectedItem.shopify_inventory_item_id) {
        const response = await axios.post(
          `${API_BASE_URL}/shopify/inventory/${selectedItem.shopify_inventory_item_id}/adjust-level`,
          {
            location_id: selectedLocationId,
            adjustment: quantityChange,
            reason: adjustmentReason || `${adjustmentType === 'add' ? 'Added' : 'Removed'} stock manually`,
            rack_number: rackNum
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success(`Stock ${adjustmentType === 'add' ? 'increased' : 'decreased'} by ${quantity} at ${response.data.location_name}${rackNum ? ` (Rack: ${rackNum})` : ''}`);
      } else {
        // Fallback to the original method
        const response = await axios.post(
          `${API_BASE_URL}/v1/inventory/${selectedItem.id}/adjust`,
          {
            quantity_change: quantityChange,
            reason: adjustmentReason || `${adjustmentType === 'add' ? 'Added' : 'Removed'} stock manually`,
            location_id: selectedLocationId || null,
            rack_number: rackNum,
            sync_to_shopify: true
          },
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'X-User-Id': user?.id || 'unknown',
              'X-User-Name': user?.full_name || user?.email || 'Unknown'
            } 
          }
        );
        
        const syncStatus = response.data?.shopify_synced ? ' (Synced to Shopify)' : '';
        toast.success(`Stock ${adjustmentType === 'add' ? 'increased' : 'decreased'} by ${quantity}${syncStatus}${rackNum ? ` (Rack: ${rackNum})` : ''}`);
      }
      
      setAdjustStockDialogOpen(false);
      setSelectedRackNumber(''); // Reset rack selection
      loadInventory();
    } catch (error) {
      toast.error(apiErr(error) || 'Failed to adjust stock');
    } finally {
      setAdjustingStock(false);
    }
  };

  const handleSetStockLevel = async (locationId, newLevel) => {
    if (!selectedItem?.shopify_inventory_item_id) {
      toast.error('This item is not linked to Shopify');
      return;
    }
    
    setAdjustingStock(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/shopify/inventory/${selectedItem.shopify_inventory_item_id}/set-level`,
        {
          location_id: locationId,
          available: newLevel
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Stock set to ${newLevel} at ${response.data.location_name}`);
      
      // Refresh location inventory
      const levelsResponse = await axios.get(
        `${API_BASE_URL}/shopify/inventory/${selectedItem.shopify_inventory_item_id}/levels`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLocationInventory(levelsResponse.data.locations || []);
      
      loadInventory();
    } catch (error) {
      toast.error(apiErr(error) || 'Failed to set stock level');
    } finally {
      setAdjustingStock(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItemForm.sku || !newItemForm.product_title) {
      toast.error('Please fill in SKU and Product Title');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/v1/inventory`,
        newItemForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Inventory item added successfully');
      setAddItemDialogOpen(false);
      setNewItemForm({
        sku: '',
        product_title: '',
        variant_title: '',
        total_available: 0,
        unit_cost: 0,
        min_stock_level: 5
      });
      loadInventory();
    } catch (error) {
      toast.error(apiErr(error) || 'Failed to add inventory item');
    }
  };

  const loadMarketplaceVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(
        `${API_BASE_URL}/marketplace/admin/onboarding-queue?status_filter=approved`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMarketplaceVendors(r.data?.rows || []);
    } catch (e) {
      // Non-fatal — Sell-By picker just hides if there are no marketplace vendors.
    }
  };

  // Bulk apply sell_by + vendor across all selected rows.
  const handleBulkAssign = async () => {
    if (selectedInventoryIds.length === 0) {
      toast.error('Select at least one item first');
      return;
    }
    if (bulkAssignForm.sell_by === 'vendor' && !bulkAssignForm.marketplace_vendor_id) {
      toast.error('Pick a marketplace vendor');
      return;
    }
    setBulkAssigning(true);
    try {
      const token = localStorage.getItem('token');
      const overrideRaw = bulkAssignForm.commission_pct_override;
      const overrideVal = overrideRaw === '' || overrideRaw === null
        ? null
        : parseFloat(overrideRaw);
      const r = await axios.post(
        `${API_BASE_URL}/enterprise/inventory/bulk-marketplace-assign`,
        {
          inventory_ids: selectedInventoryIds,
          sell_by: bulkAssignForm.sell_by,
          marketplace_vendor_id:
            bulkAssignForm.sell_by === 'vendor' ? bulkAssignForm.marketplace_vendor_id : null,
          commission_pct_override: overrideVal,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Updated ${r.data.updated_count} item${r.data.updated_count === 1 ? '' : 's'}`);
      if (r.data.missing_ids?.length) {
        toast.message(`${r.data.missing_ids.length} item(s) not found / different tenant`);
      }
      setBulkAssignOpen(false);
      setSelectedInventoryIds([]);
      setBulkAssignForm({ sell_by: 'vendor', marketplace_vendor_id: '', commission_pct_override: '' });
      loadInventory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk assign failed');
    } finally {
      setBulkAssigning(false);
    }
  };

  // Toggle a single row in the selection set.
  const toggleRowSelected = (id) => {
    setSelectedInventoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Header checkbox — select/deselect every row currently in view.
  const toggleSelectAllVisible = () => {
    const visibleIds = (items || []).map((i) => i.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedInventoryIds.includes(id));
    if (allSelected) {
      setSelectedInventoryIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedInventoryIds(Array.from(new Set([...selectedInventoryIds, ...visibleIds])));
    }
  };

  const openEditDialog = (item) => {
    setSelectedItem(item);
    setEditForm({
      // Show the same cost the table shows (unless it comes from a vendor
      // mapping, which is managed in the Vendors tab, not here).
      unit_cost: item.unit_cost || (item.cost_source !== 'vendor' ? item.cost_price : 0) || 0,
      min_stock_level: item.min_stock_level || 0,
      reorder_point: item.reorder_point || 0,
      reorder_quantity: item.reorder_quantity || 0,
      made_to_order: !!item.made_to_order,
      production_days: item.production_days || 0,
      processing_days: item.processing_days || 0,
      express_eligible: !!item.express_eligible,
      express_sla_hours: item.express_sla_hours || 0,
      package_id: item.package_id || null,
      virtual_enabled: !!item.virtual_enabled,
      virtual_stock: item.virtual_stock || 0,
      sell_by: item.sell_by || 'company',
      marketplace_vendor_id: item.marketplace_vendor_id || null,
      commission_pct_override:
        item.commission_pct_override === undefined || item.commission_pct_override === null
          ? ''
          : item.commission_pct_override,
    });
    setSyncToShopify(false);
    setEditDialogOpen(true);
    // Lazy-load approved marketplace vendors only when the modal opens.
    if (marketplaceVendors.length === 0) loadMarketplaceVendors();
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const token = localStorage.getItem('token');
      // Pydantic can't distinguish `package_id: null` (unassign) from
      // `package_id: omitted`, so send an explicit `clear_package_id`
      // flag when the user picks "No package assigned".
      const payload = {
        ...editForm,
        sync_to_shopify: syncToShopify,
        clear_package_id: editForm.package_id === null || editForm.package_id === '',
      };
      await axios.put(
        `${API_BASE_URL}/v1/inventory/${selectedItem.id}/settings`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Virtual stock has its own dedicated endpoint (separate concern,
      // keeps the legacy settings PUT untouched). Save in parallel only
      // when the toggle/value actually changed.
      if (
        editForm.virtual_enabled !== !!selectedItem.virtual_enabled ||
        Number(editForm.virtual_stock || 0) !== Number(selectedItem.virtual_stock || 0)
      ) {
        await axios.patch(
          `${API_BASE_URL}/v1/inventory/${selectedItem.sku}/virtual`,
          {
            virtual_enabled: !!editForm.virtual_enabled,
            virtual_stock: parseInt(editForm.virtual_stock, 10) || 0,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // Marketplace assignment — only call when the admin actually
      // changed the sell-by mapping or the commission override. The
      // backend handles toggling between company/vendor and clears
      // vendor fields when reverting.
      const sellByChanged = (editForm.sell_by || 'company') !== (selectedItem.sell_by || 'company');
      const vendorChanged = (editForm.marketplace_vendor_id || null) !== (selectedItem.marketplace_vendor_id || null);
      const overrideChanged =
        String(editForm.commission_pct_override ?? '') !==
        String(selectedItem.commission_pct_override ?? '');
      if (sellByChanged || vendorChanged || overrideChanged) {
        const overrideVal =
          editForm.commission_pct_override === '' || editForm.commission_pct_override === null
            ? null
            : parseFloat(editForm.commission_pct_override);
        await axios.put(
          `${API_BASE_URL}/enterprise/inventory/${selectedItem.id}/marketplace-assign`,
          {
            sell_by: editForm.sell_by || 'company',
            marketplace_vendor_id:
              (editForm.sell_by || 'company') === 'vendor' ? editForm.marketplace_vendor_id : null,
            commission_pct_override: overrideVal,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      toast.success(syncToShopify ? 'Settings saved and synced to Shopify' : 'Settings saved successfully');
      setEditDialogOpen(false);
      loadInventory();
    } catch (error) {
      toast.error(apiErr(error) || 'Failed to save settings');
    } finally {
      setSavingEdit(false);
    }
  };

  // Save vendor mapping
  const handleSaveVendorMapping = async () => {
    if (!vendorMappingForm.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }
    if (!vendorMappingForm.cost_price || parseFloat(vendorMappingForm.cost_price) <= 0) {
      toast.error('Please enter a valid cost price');
      return;
    }
    
    setSavingMapping(true);
    try {
      const token = localStorage.getItem('token');
      const selectedVendor = vendors.find(v => v.id === vendorMappingForm.vendor_id);
      
      await axios.post(
        `${API_BASE_URL}/enterprise/product-vendors`,
        {
          sku: selectedItem?.sku || '',
          product_id: selectedItem?.shopify_product_id || selectedItem?.product_id || '',
          variant_id: selectedItem?.variant_id || selectedItem?.shopify_variant_id || '',
          product_title: selectedItem?.product_title || selectedItem?.name || '',
          vendor_id: vendorMappingForm.vendor_id,
          vendor_name: selectedVendor?.vendor_name || '',
          vendor_sku: vendorMappingForm.vendor_sku || selectedItem?.sku || '',
          cost_price: parseFloat(vendorMappingForm.cost_price) || 0,
          moq: parseInt(vendorMappingForm.moq) || 1,
          lead_time_days: parseInt(vendorMappingForm.lead_time_days) || 7
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Vendor mapping saved successfully');
      setVendorMappingDialogOpen(false);
      setVendorMappingForm({
        vendor_id: '',
        vendor_sku: '',
        cost_price: '',
        moq: 1,
        lead_time_days: 7
      });
      // Reload inventory to reflect changes
      loadInventory();
    } catch (error) {
      console.error('Vendor mapping error:', error);
      // Handle Pydantic validation errors (array of objects) or string error
      let errorMessage = 'Failed to save vendor mapping';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error format: [{loc: [...], msg: "...", type: "..."}]
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setSavingMapping(false);
    }
  };

  // Fetch adjustment history for an item
  const fetchAdjustmentHistory = async (item) => {
    setAdjustmentHistoryItem(item);
    setAdjustmentHistoryDialogOpen(true);
    setLoadingHistory(true);
    setAdjustmentHistory([]);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/enterprise/inventory/adjustments/by-sku/${encodeURIComponent(item.sku)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAdjustmentHistory(response.data.adjustments || []);
    } catch (error) {
      console.error('Failed to fetch adjustment history:', error);
      toast.error('Failed to load adjustment history');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Cost-history modal: fetch all cost-change events for this SKU.
  const fetchCostHistory = async (item) => {
    setCostHistoryItem(item);
    setCostHistoryDialogOpen(true);
    setLoadingCostHistory(true);
    setCostHistoryData(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/enterprise/inventory/${encodeURIComponent(item.sku)}/cost-history`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCostHistoryData(response.data);
    } catch (error) {
      console.error('Failed to fetch cost history:', error);
      toast.error('Failed to load cost history');
      setCostHistoryData({ sku: item.sku, total_changes: 0, rows: [] });
    } finally {
      setLoadingCostHistory(false);
    }
  };

  // One-shot bulk load — populates the "cost changed recently" chip on
  // each inventory row without per-row API calls.
  const loadRecentCostChanges = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/enterprise/inventory/cost-history/recent-changes?days=30&limit=1000`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const map = {};
      (response.data?.rows || []).forEach((r) => {
        if (r.sku) map[r.sku] = r;
      });
      setRecentCostChangeSkus(map);
    } catch (error) {
      // Not critical — silently swallow so the page never breaks
      // because of the chip.
    }
  };

  // Format adjustment reason
  const formatAdjustmentReason = (reason) => {
    const reasons = {
      'order': 'Sales Order',
      'order_cancelled': 'Order Cancelled',
      'return': 'Return/Refund',
      'inwarding': 'Inwarding',
      'manual': 'Manual Adjustment',
      'transfer': 'Transfer',
      'grn': 'GRN Receipt',
      'damage': 'Damage/Loss',
      'correction': 'Correction'
    };
    return reasons[reason] || reason;
  };

  const getStockStatus = (item) => {
    // Prefer `sellable_quantity` (= total_available − blocked_by_orders)
    // when the backend overlays it. Falls back to `total_available` for
    // legacy / unreserved rows so the badge still makes sense when the
    // omni-channel reservation overlay isn't populated.
    const available = (
      item.sellable_quantity !== undefined && item.sellable_quantity !== null
        ? item.sellable_quantity
        : (item.total_available || item.quantity || 0)
    );
    const reorderPoint = item.reorder_point || item.min_stock_level || 0;
    const trackQuantity = item.track_quantity !== false; // Default to true
    const continueSelling = item.continue_selling_when_out_of_stock || item.inventory_policy === 'continue';
    
    // If tracking is disabled, show "Not Tracked"
    if (!trackQuantity) {
      return { label: 'Not Tracked', color: 'bg-gray-500/20 text-gray-400' };
    }
    
    // If out of stock but continue selling is enabled
    if (available <= 0 && continueSelling) {
      return { label: 'Overselling', color: 'bg-blue-500/20 text-blue-400' };
    }
    
    if (available <= 0) return { label: 'Out of Stock', color: 'bg-red-500/20 text-red-400' };
    if (available <= reorderPoint) return { label: 'Low Stock', color: 'bg-orange-500/20 text-orange-400' };
    return { label: 'In Stock', color: 'bg-green-500/20 text-green-400' };
  };

  // Items are already filtered by server-side search
  const filteredItems = items;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-playfair text-wt-text-primary">Inventory Master</h1>
          <p className="text-wt-text-muted mt-1">Manage products, stock levels, and vendor mappings</p>
        </div>
        <div className="flex gap-3">
          {zohoConnected && (
            <Button
              onClick={() => handleSyncToZoho(false)}
              disabled={syncingToZoho}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {syncingToZoho ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CloudUpload className="h-4 w-4 mr-2" />
              )}
              {syncingToZoho ? 'Syncing...' : 'Sync to Zoho'}
            </Button>
          )}
          <Button 
            onClick={() => setAddItemDialogOpen(true)}
            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
            data-testid="add-inventory-item-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        </div>
      </div>

      {/* Summary Cards — Inventory Value widget removed Feb-6-2026
          per merchant request (cost data is sensitive and exposed on
          the row-level Cost column when needed). Grid is now 3-up;
          stays 1-col on mobile. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Boxes className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Total Products</p>
              <p className="text-2xl font-bold text-wt-text-primary">{summary.total_items}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <MapPin className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Locations</p>
              <p className="text-2xl font-bold text-wt-text-primary">{locations.length}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-wt-paper border border-orange-500/30 rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/10">
              <AlertTriangle className="h-5 w-5 text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Low Stock Items</p>
              <p className="text-2xl font-bold text-orange-400">{summary.low_stock_count}</p>
            </div>
          </div>
        </motion.div>

        {zohoSyncStatus && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-wt-paper border border-blue-500/30 rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <CloudUpload className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-wt-text-muted">Zoho Sync</p>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-green-400">{zohoSyncStatus.synced}</span>
                  <span className="text-wt-text-muted text-sm">/</span>
                  <span className="text-lg font-bold text-wt-text-primary">{zohoSyncStatus.total}</span>
                </div>
                {zohoSyncStatus.not_synced > 0 && (
                  <p className="text-xs text-yellow-400">{zohoSyncStatus.not_synced} pending</p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
          <Input
            placeholder="Search by SKU, product name, or barcode..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadInventory(1, false)}
            className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary"
          />
        </div>
        
        <Select value={locationFilter} onValueChange={setLocationFilter}>
          <SelectTrigger className="w-[180px] bg-wt-bg border-wt-border text-wt-text-primary">
            <SelectValue placeholder="All Locations" />
          </SelectTrigger>
          <SelectContent className="bg-wt-paper border-wt-border">
            <SelectItem value="all">All Locations</SelectItem>
            {locations.map(loc => (
              <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={stockFilter} onValueChange={setStockFilter}>
          <SelectTrigger className="w-[150px] bg-wt-bg border-wt-border text-wt-text-primary">
            <SelectValue placeholder="Stock Status" />
          </SelectTrigger>
          <SelectContent className="bg-wt-paper border-wt-border">
            <SelectItem value="all">All Stock</SelectItem>
            <SelectItem value="in">In Stock</SelectItem>
            <SelectItem value="low">Low Stock</SelectItem>
            <SelectItem value="out">Out of Stock</SelectItem>
          </SelectContent>
        </Select>

        <Button onClick={() => loadInventory(1, false)} variant="outline" className="border-wt-border text-wt-text-primary">
          <Filter className="h-4 w-4 mr-2" />
          Apply
        </Button>
      </div>

      {/* Bulk action bar — visible only when one or more rows are
          selected. Powered by the bulk-marketplace-assign endpoint
          (Feb 2026 ask). */}
      {selectedInventoryIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-wt-gold/10 border border-wt-gold/30 rounded-xl px-4 py-3" data-testid="inventory-bulk-action-bar">
          <span className="text-sm text-wt-gold font-semibold">
            {selectedInventoryIds.length} item{selectedInventoryIds.length === 1 ? '' : 's'} selected
          </span>
          <Button
            size="sm"
            onClick={() => {
              setBulkAssignForm({ sell_by: 'vendor', marketplace_vendor_id: '', commission_pct_override: '' });
              setBulkAssignOpen(true);
            }}
            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
            data-testid="bulk-assign-vendor-btn"
          >
            <Building2 className="h-4 w-4 mr-1" />
            Assign to Marketplace Vendor
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setBulkAssignForm({ sell_by: 'company', marketplace_vendor_id: '', commission_pct_override: '' });
              setBulkAssignOpen(true);
            }}
            className="border-wt-border text-wt-text-primary"
            data-testid="bulk-unassign-vendor-btn"
          >
            Mark as Company-sold
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => openLabelDialog(items.filter(it => selectedInventoryIds.includes(it.id)))}
            className="border-wt-gold/50 text-wt-gold hover:bg-wt-gold/10"
            data-testid="bulk-print-labels-btn"
          >
            <Printer className="h-4 w-4 mr-1" />
            Print Labels
          </Button>
          <button
            onClick={() => setSelectedInventoryIds([])}
            className="ml-auto text-xs text-wt-text-muted hover:text-wt-text-primary"
            data-testid="bulk-clear-selection"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Inventory Table */}
      <div className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-wt-subtle border-b border-wt-border">
              <tr>
                <th className="text-center p-4 w-10">
                  <input
                    type="checkbox"
                    className="accent-wt-gold cursor-pointer"
                    checked={
                      (items || []).length > 0 &&
                      (items || []).every((it) => selectedInventoryIds.includes(it.id))
                    }
                    onChange={toggleSelectAllVisible}
                    data-testid="inventory-select-all"
                    title="Select all visible items"
                  />
                </th>
                <th className="text-left p-4 text-wt-text-muted font-medium">Product</th>
                <th className="text-left p-4 text-wt-text-muted font-medium">SKU</th>
                <th className="text-center p-4 text-wt-text-muted font-medium">Total Stock</th>
                <th className="text-center p-4 text-wt-text-muted font-medium">Available</th>
                <th className="text-center p-4 text-wt-text-muted font-medium">Status</th>
                {coverageChannels.length > 0 && (
                  <th className="text-center p-4 text-wt-text-muted font-medium">Channels</th>
                )}
                <th className="text-right p-4 text-wt-text-muted font-medium">Cost</th>
                <th className="text-center p-4 text-wt-text-muted font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item, index) => {
                const status = getStockStatus(item);
                const isExpanded = expandedItems[item.id];
                
                return (
                  <React.Fragment key={item.id}>
                    <tr className={`border-b border-wt-border/50 hover:bg-wt-subtle/50 ${index % 2 === 0 ? 'bg-wt-bg/30' : ''}`}>
                      <td className="p-4 text-center">
                        <input
                          type="checkbox"
                          className="accent-wt-gold cursor-pointer"
                          checked={selectedInventoryIds.includes(item.id)}
                          onChange={() => toggleRowSelected(item.id)}
                          data-testid={`inventory-row-select-${item.sku || item.id}`}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {item.image_url ? (
                            <ImageLightbox 
                              src={item.image_url} 
                              alt={item.product_title || item.name}
                              className="w-12 h-12"
                              thumbnailClassName="w-12 h-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-wt-subtle flex items-center justify-center">
                              <ImageIcon className="h-5 w-5 text-wt-text-muted" />
                            </div>
                          )}
                          <div>
                            <p className="text-wt-text-primary font-medium line-clamp-1">{item.product_title || item.name}</p>
                            {item.variant_title && (
                              <p className="text-sm text-wt-text-muted">{item.variant_title}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <code className="text-sm text-wt-gold bg-wt-gold/10 px-2 py-1 rounded">{item.sku}</code>
                            {item.shopify_delisted && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-zinc-500/15 text-zinc-300 border border-zinc-500/30 leading-none"
                                title="This product was deleted on Shopify; the master SKU is kept for your other channels"
                                data-testid={`not-on-shopify-badge-${item.sku}`}
                              >
                                Not on Shopify
                              </span>
                            )}
                            {item.virtual_enabled && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-500/15 text-blue-300 border border-blue-500/30 leading-none"
                                title={`Virtual stock buffer: ${item.virtual_stock || 0}`}
                                data-testid={`virtual-badge-${item.sku}`}
                              >
                                <Sparkles className="h-2.5 w-2.5" />
                                V:{item.virtual_stock || 0}
                              </span>
                            )}
                            {recentCostChangeSkus[item.sku] && (
                              <button
                                type="button"
                                onClick={() => fetchCostHistory(item)}
                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none border cursor-pointer hover:brightness-110 ${
                                  (recentCostChangeSkus[item.sku].total_delta || 0) > 0
                                    ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                                    : (recentCostChangeSkus[item.sku].total_delta || 0) < 0
                                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                    : 'bg-slate-500/15 text-slate-300 border-slate-500/30'
                                }`}
                                title={`${recentCostChangeSkus[item.sku].change_count} cost change${recentCostChangeSkus[item.sku].change_count === 1 ? '' : 's'} in the last 30 days. Click to view full history.`}
                                data-testid={`cost-change-chip-${item.sku}`}
                              >
                                <DollarSign className="h-2.5 w-2.5" />
                                {(recentCostChangeSkus[item.sku].total_delta || 0) > 0 ? '↑' : (recentCostChangeSkus[item.sku].total_delta || 0) < 0 ? '↓' : '='}
                                {Math.abs(recentCostChangeSkus[item.sku].total_delta || 0).toFixed(2)}
                              </button>
                            )}
                            {item.sell_by === 'vendor' && (() => {
                              // Marketplace chip — show vendor name beside
                              // SKU so admins can spot a vendor's catalog
                              // at a glance. Falls back to id lookup when
                              // the row doesn't have marketplace_vendor_name
                              // cached.
                              const vName = item.marketplace_vendor_name
                                || (marketplaceVendors.find((v) => v.id === item.marketplace_vendor_id)?.vendor_name)
                                || 'Marketplace';
                              return (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none border bg-purple-500/15 text-purple-300 border-purple-500/30 max-w-[180px]"
                                  title={`Sold by marketplace vendor: ${vName}${item.commission_pct_override !== null && item.commission_pct_override !== undefined ? ` · ${item.commission_pct_override}% commission` : ''}`}
                                  data-testid={`marketplace-chip-${item.sku}`}
                                >
                                  <Building2 className="h-2.5 w-2.5 shrink-0" />
                                  <span className="truncate">MP · {vName}</span>
                                </span>
                              );
                            })()}
                          </div>
                          {item.shopify_variant_id && (
                            <code
                              className="text-[10px] text-wt-text-muted/80 font-mono leading-none"
                              title="Shopify variant ID — useful for spotting duplicate SKUs that come from Shopify"
                              data-testid={`variant-id-${item.sku}-${item.shopify_variant_id}`}
                            >
                              V·ID: {item.shopify_variant_id}
                            </code>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <span className="text-wt-text-primary font-semibold">{item.total_quantity || item.quantity || 0}</span>
                      </td>
                      <td className="p-4 text-center">
                        {(() => {
                          // Show the SELLABLE figure (total_available −
                          // reservations blocked by open channel orders)
                          // when the backend overlays it; otherwise fall
                          // back to the raw physical count.
                          const hasOverlay = item.sellable_quantity !== undefined && item.sellable_quantity !== null;
                          const sellable = hasOverlay
                            ? item.sellable_quantity
                            : (item.total_available || item.quantity || 0);
                          const blocked = Number(item.blocked_quantity || item.total_reserved || 0);
                          return (
                            <>
                              <span
                                className="text-wt-text-primary"
                                data-testid={`inventory-row-available-${item.sku || ''}`}
                                title={
                                  blocked > 0
                                    ? `Sellable = total_available (${item.total_available || 0}) − blocked by orders (${blocked})`
                                    : undefined
                                }
                              >
                                {sellable}
                              </span>
                              {blocked > 0 && (
                                <button
                                  type="button"
                                  className="ml-1 text-xs text-orange-400 hover:text-orange-300 underline decoration-dotted underline-offset-2 cursor-pointer"
                                  data-testid={`inventory-row-blocked-${item.sku || ''}`}
                                  title={`${blocked} unit${blocked === 1 ? '' : 's'} blocked by open orders — click to see which orders`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    loadBlockedOrders(item.sku);
                                  }}
                                >
                                  🔒 {blocked} blocked
                                </button>
                              )}
                              {item.virtual_enabled && (item.virtual_stock || 0) > 0 && (
                                <span
                                  className="block text-[10px] text-blue-300/90 mt-0.5"
                                  title={`Sellable ${sellable} + virtual ${item.virtual_stock}`}
                                >
                                  +{item.virtual_stock} virtual
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td className="p-4 text-center">
                        <Badge className={status.color}>{status.label}</Badge>
                      </td>
                      {coverageChannels.length > 0 && (
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-1 flex-wrap max-w-[220px] mx-auto" data-testid={`channel-coverage-${item.sku}`}>
                            {coverageChannels.map((ch) => {
                              const listed = (channelCoverage[item.sku] || []).includes(ch.channel_type);
                              const abbr = { shopify: 'SH', danube: 'DH', noon: 'NN', homecentre: 'HC', amazon_in: 'A·IN', amazon_ae: 'A·AE', flipkart: 'FK' }[ch.channel_type]
                                || ch.channel_type.slice(0, 2).toUpperCase();
                              return (
                                <span
                                  key={ch.channel_type}
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none border ${
                                    listed
                                      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                      : 'bg-zinc-600/10 text-zinc-500 border-zinc-600/20 line-through decoration-zinc-500/50'
                                  }`}
                                  title={listed ? `Listed on ${ch.name}` : `Not listed on ${ch.name}`}
                                  data-testid={`coverage-${item.sku}-${ch.channel_type}-${listed ? 'listed' : 'missing'}`}
                                >
                                  {abbr}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                      )}
                      <td className="p-4 text-right">
                        <span className="text-wt-text-primary">{formatCurrency(item.cost_price)}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleItemExpand(item.id)}
                            className="text-wt-text-muted hover:text-wt-text-primary"
                          >
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </Button>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-wt-text-muted hover:text-wt-text-primary"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-wt-paper border-wt-border w-48">
                              <DropdownMenuItem 
                                onClick={() => openAdjustStockDialog(item)}
                                className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                              >
                                <PlusCircle className="h-4 w-4 mr-2 text-green-400" />
                                Adjust Stock
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => fetchAdjustmentHistory(item)}
                                className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                              >
                                <History className="h-4 w-4 mr-2 text-blue-400" />
                                Stock History
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => fetchCostHistory(item)}
                                className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                                data-testid={`cost-history-${item.sku}`}
                              >
                                <DollarSign className="h-4 w-4 mr-2 text-amber-400" />
                                Cost History
                                {recentCostChangeSkus[item.sku] && (
                                  <span className="ml-auto text-[10px] text-amber-300 font-semibold">
                                    {recentCostChangeSkus[item.sku].change_count}↻
                                  </span>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openLabelDialog([item])}
                                className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                                data-testid={`print-label-${item.sku}`}
                              >
                                <Printer className="h-4 w-4 mr-2 text-wt-gold" />
                                Print Label
                              </DropdownMenuItem>
                              {!item.shopify_variant_id && shopifyConnected && (
                                <DropdownMenuItem
                                  onClick={async () => {
                                    if (!window.confirm(`Publish "${item.product_title || item.sku}" to Shopify as a new product?`)) return;
                                    try {
                                      const token = localStorage.getItem('token');
                                      const r = await axios.post(`${API_BASE_URL}/shopify/products/publish/${encodeURIComponent(item.sku)}`, {},
                                        { headers: { Authorization: `Bearer ${token}` } });
                                      toast.success('Published to Shopify');
                                      if (r.data.admin_url) window.open(r.data.admin_url, '_blank');
                                      loadInventory();
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Publish failed');
                                    }
                                  }}
                                  className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                                  data-testid={`publish-shopify-${item.sku}`}
                                >
                                  <ExternalLink className="h-4 w-4 mr-2 text-emerald-400" />
                                  Publish to Shopify
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator className="bg-wt-border" />
                              <DropdownMenuItem 
                                onClick={() => {
                                  try {
                                    setSelectedItem(item);
                                    setVendorMappingForm({
                                      vendor_id: item?.primary_vendor_id || '',
                                      vendor_sku: item?.sku || '',
                                      cost_price: item?.cost_price || item?.unit_cost || '',
                                      moq: item?.moq || 1,
                                      lead_time_days: item?.lead_time_days || 7
                                    });
                                    setVendorMappingDialogOpen(true);
                                  } catch (e) {
                                    console.error('Error opening vendor mapping:', e);
                                    toast.error('Failed to open vendor mapping');
                                  }
                                }}
                                className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                              >
                                <Building2 className="h-4 w-4 mr-2 text-wt-gold" />
                                Vendor Mapping
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openEditDialog(item)}
                                className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                              >
                                <Edit2 className="h-4 w-4 mr-2" />
                                Edit Settings
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-wt-border" />
                              {item.zoho_item_id ? (
                                <DropdownMenuItem className="text-blue-400 cursor-default" disabled>
                                  <Check className="h-4 w-4 mr-2" />
                                  Synced to Zoho
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem 
                                  onClick={() => handleSyncSingleItemToZoho(item.id)}
                                  className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                                >
                                  <CloudUpload className="h-4 w-4 mr-2 text-blue-400" />
                                  Sync to Zoho
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded Location Details */}
                    {isExpanded && (
                      <tr className="bg-wt-subtle/30">
                        <td colSpan={coverageChannels.length > 0 ? 9 : 8} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-wt-gold" />
                                Stock by Location
                              </h4>
                              <div className="space-y-1">
                                {/* Show all locations with their stock levels (like Shopify) */}
                                {locations.map((loc, i) => {
                                  // Find matching location data for this item
                                  const locationStock = item.locations?.find(l => 
                                    String(l.location_id) === String(loc.id) || 
                                    String(l.shopify_location_id) === String(loc.id) ||
                                    l.location_name === loc.name
                                  );
                                  const qty = locationStock?.quantity ?? locationStock?.available ?? 0;
                                  
                                  return (
                                    <div key={i} className="flex justify-between text-sm p-2 bg-wt-paper rounded hover:bg-wt-subtle/50 transition-colors">
                                      <span className="text-wt-text-muted">{loc.name}</span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedItem(item);
                                          loadRackInventory(item.sku, loc.id, loc.name);
                                        }}
                                        className={`font-medium hover:underline cursor-pointer ${qty > 0 ? 'text-green-400 hover:text-green-300' : 'text-wt-text-muted hover:text-wt-text-primary'}`}
                                        data-testid={`rack-inventory-btn-${item.sku}-${loc.id}`}
                                        title="Click to view rack-wise breakdown"
                                      >
                                        {qty}
                                      </button>
                                    </div>
                                  );
                                })}
                                {locations.length === 0 && (
                                  <p className="text-sm text-wt-text-muted">No locations configured</p>
                                )}
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-wt-text-primary">Stock Settings</h4>
                              <div className="text-sm space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-wt-text-muted">Min Stock Level:</span>
                                  <span className="text-wt-text-primary">{item.min_stock_level || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-wt-text-muted">Reorder Point:</span>
                                  <span className="text-wt-text-primary">{item.reorder_point || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-wt-text-muted">Reorder Qty:</span>
                                  <span className="text-wt-text-primary">{item.reorder_quantity || 0}</span>
                                </div>
                                {/* Shopify Settings */}
                                <div className="border-t border-wt-border/50 pt-2 mt-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-wt-text-muted">Track Quantity:</span>
                                    <Badge className={item.track_quantity !== false ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                                      {item.track_quantity !== false ? 'Yes' : 'No'}
                                    </Badge>
                                  </div>
                                  <div className="flex justify-between items-center mt-1">
                                    <span className="text-wt-text-muted">Continue Selling:</span>
                                    <Badge className={item.continue_selling_when_out_of_stock || item.inventory_policy === 'continue' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}>
                                      {item.continue_selling_when_out_of_stock || item.inventory_policy === 'continue' ? 'Yes' : 'No'}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-wt-text-primary">Vendor Info</h4>
                              {item.primary_vendor_name ? (
                                <div className="text-sm">
                                  <p className="text-wt-text-primary">{item.primary_vendor_name}</p>
                                  <p className="text-wt-text-muted">Primary Vendor</p>
                                </div>
                              ) : (
                                <p className="text-sm text-orange-400">No vendor mapped</p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredItems.length === 0 && (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-wt-text-muted mx-auto mb-4" />
            <p className="text-wt-text-muted">No inventory items found</p>
            <p className="text-xs text-wt-text-muted mt-2">
              Sync products from Integrations → Shopify, or click "Add Item" to create one.
            </p>
          </div>
        )}
        
        {/* Pagination Info & Load More */}
        {filteredItems.length > 0 && (
          <div className="p-4 border-t border-wt-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-wt-text-muted">
              Showing {items.length} of {pagination.total_count || summary.total_items} items
              {pagination.total_pages > 1 && ` (Page ${pagination.page} of ${pagination.total_pages})`}
            </p>
            {pagination.has_more && (
              <Button
                onClick={loadMoreItems}
                disabled={loadingMore}
                variant="outline"
                className="border-wt-gold text-wt-gold hover:bg-wt-gold/10"
                data-testid="load-more-btn"
              >
                {loadingMore ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
                ) : (
                  <>Load More ({(pagination.total_count || summary.total_items) - items.length} remaining)</>
                )}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Bulk Marketplace-Assign Dialog (Feb 2026) */}
      {/* Print SKU labels dialog */}
      <Dialog open={labelDialog.open} onOpenChange={(open) => !open && setLabelDialog(p => ({ ...p, open: false }))}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md" data-testid="print-labels-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Printer className="h-5 w-5 text-wt-gold" />
              Print SKU Labels
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {labelDialog.items.map(it => (
                <div key={it.sku} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-wt-text-primary truncate" title={it.name}>{it.name}</p>
                    <p className="text-xs text-wt-text-muted">{it.sku}</p>
                  </div>
                  <Input
                    type="number" min="0" max="500"
                    value={it.qty}
                    onChange={(e) => setLabelQty(it.sku, e.target.value)}
                    className="w-20 shrink-0 bg-wt-bg border-wt-border text-wt-text-primary"
                    data-testid={`label-qty-${it.sku}`}
                  />
                </div>
              ))}
            </div>
            <p className="text-[11px] text-wt-text-muted">Size, barcode type and name crop come from <span className="text-wt-gold">Settings → Label Settings</span></p>
            <Button
              onClick={printLabels}
              disabled={labelDialog.loading}
              className="w-full bg-wt-gold hover:bg-wt-gold-hover text-black"
              data-testid="print-labels-submit-btn"
            >
              {labelDialog.loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}
              Generate Labels PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent
          className="bg-wt-paper border-wt-border max-w-md"
          data-testid="bulk-assign-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Building2 className="h-5 w-5 text-wt-gold" />
              Bulk Update {selectedInventoryIds.length} Item{selectedInventoryIds.length === 1 ? '' : 's'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-wt-text-primary">Sell-by</Label>
              <div className="flex items-center gap-4 mt-2 text-sm">
                <label className="inline-flex items-center gap-2 text-wt-text-primary cursor-pointer">
                  <input
                    type="radio"
                    name="bulk-sell-by"
                    checked={bulkAssignForm.sell_by === 'company'}
                    onChange={() => setBulkAssignForm({ ...bulkAssignForm, sell_by: 'company', marketplace_vendor_id: '', commission_pct_override: '' })}
                    className="accent-wt-gold"
                    data-testid="bulk-sell-by-company"
                  />
                  Company (own stock)
                </label>
                <label className="inline-flex items-center gap-2 text-wt-text-primary cursor-pointer">
                  <input
                    type="radio"
                    name="bulk-sell-by"
                    checked={bulkAssignForm.sell_by === 'vendor'}
                    onChange={() => setBulkAssignForm({ ...bulkAssignForm, sell_by: 'vendor' })}
                    className="accent-wt-gold"
                    data-testid="bulk-sell-by-vendor"
                  />
                  Marketplace vendor
                </label>
              </div>
            </div>

            {bulkAssignForm.sell_by === 'vendor' && (
              <>
                <div>
                  <Label className="text-wt-text-primary">Marketplace Vendor *</Label>
                  <select
                    value={bulkAssignForm.marketplace_vendor_id}
                    onChange={(e) => setBulkAssignForm({ ...bulkAssignForm, marketplace_vendor_id: e.target.value })}
                    disabled={marketplaceVendors.length === 0}
                    className="mt-1 w-full px-3 py-2 rounded bg-wt-bg border border-wt-border text-wt-text-primary text-sm disabled:opacity-50"
                    data-testid="bulk-vendor-select"
                  >
                    <option value="">— Select vendor —</option>
                    {marketplaceVendors.map((v) => (
                      <option key={v.id} value={v.id}>{v.vendor_name}</option>
                    ))}
                  </select>
                  {marketplaceVendors.length === 0 && (
                    <p className="text-xs text-amber-400 mt-1">No approved marketplace vendors yet.</p>
                  )}
                </div>
                <div>
                  <Label className="text-wt-text-primary">Commission % override (optional)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="Leave empty to use vendor default"
                    value={bulkAssignForm.commission_pct_override}
                    onChange={(e) => setBulkAssignForm({ ...bulkAssignForm, commission_pct_override: e.target.value })}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                    data-testid="bulk-commission-override"
                  />
                </div>
              </>
            )}

            <div className="text-xs text-wt-text-muted bg-wt-subtle/40 rounded p-3">
              {bulkAssignForm.sell_by === 'vendor'
                ? `This will mark ${selectedInventoryIds.length} item(s) as sold via the selected marketplace vendor and re-price them on Shopify (cost × (1 + commission%)).`
                : `This will convert ${selectedInventoryIds.length} item(s) back to company-sold and strip any vendor assignment.`}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setBulkAssignOpen(false)} className="text-wt-text-muted">
                Cancel
              </Button>
              <Button
                onClick={handleBulkAssign}
                disabled={bulkAssigning || (bulkAssignForm.sell_by === 'vendor' && !bulkAssignForm.marketplace_vendor_id)}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                data-testid="bulk-assign-confirm"
              >
                {bulkAssigning ? 'Applying…' : 'Apply to all'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent
          className="bg-wt-paper border-wt-border max-w-lg max-h-[90vh] overflow-y-auto"
          data-testid="edit-inventory-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-wt-gold" />
              Edit Product Settings
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="p-3 bg-wt-subtle rounded-lg">
                <p className="text-wt-text-primary font-medium">{selectedItem.product_title}</p>
                {selectedItem.variant_title && (
                  <p className="text-sm text-wt-text-muted">{selectedItem.variant_title}</p>
                )}
                <p className="text-wt-gold font-mono text-sm mt-1">SKU: {selectedItem.sku}</p>
              </div>
              
              {/* Unit Cost - Main field */}
              <div className="p-4 border border-wt-gold/30 rounded-lg bg-wt-gold/5">
                <Label className="text-wt-gold font-medium flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Unit Cost (Purchase Price)
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.unit_cost}
                  onChange={(e) => setEditForm({...editForm, unit_cost: parseFloat(e.target.value) || 0})}
                  className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary text-lg"
                  data-testid="edit-unit-cost"
                />
                <p className="text-xs text-wt-text-muted mt-1">
                  The cost price for purchasing this item from vendors
                </p>
              </div>
              
              {/* Stock Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-wt-text-primary">Min Stock Level</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editForm.min_stock_level}
                    onChange={(e) => setEditForm({...editForm, min_stock_level: parseInt(e.target.value) || 0})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-primary">Reorder Point</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editForm.reorder_point}
                    onChange={(e) => setEditForm({...editForm, reorder_point: parseInt(e.target.value) || 0})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-wt-text-primary">Reorder Quantity</Label>
                <Input
                  type="number"
                  min="0"
                  value={editForm.reorder_quantity}
                  onChange={(e) => setEditForm({...editForm, reorder_quantity: parseInt(e.target.value) || 0})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>

              {/* Made to Order — production settings */}
              <div className="p-4 border border-amber-500/30 rounded-lg bg-amber-500/5" data-testid="made-to-order-section">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label className="text-amber-400 font-medium">Made to Order</Label>
                    <p className="text-xs text-wt-text-muted mt-1">
                      Manufactured after the order is placed. Used to auto-calculate the production ETA and show "Under Manufacturing" on customer tracking.
                    </p>
                  </div>
                  <Switch
                    checked={!!editForm.made_to_order}
                    onCheckedChange={(v) => setEditForm({...editForm, made_to_order: v})}
                    data-testid="made-to-order-toggle"
                  />
                </div>
                {!editForm.made_to_order && (
                  <div className="mt-3">
                    <Label className="text-wt-text-primary">Processing days <span className="text-wt-text-muted font-normal">(in-stock handling: pick/pack time, used for the customer delivery estimate)</span></Label>
                    <Input
                      type="number"
                      min="0"
                      value={editForm.processing_days}
                      onChange={(e) => setEditForm({...editForm, processing_days: parseInt(e.target.value) || 0})}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                      placeholder="0 = use store default"
                      data-testid="processing-days-input"
                    />
                  </div>
                )}
                {editForm.made_to_order && (
                  <div className="mt-3">
                    <Label className="text-wt-text-primary">Production days</Label>
                    <Input
                      type="number"
                      min="0"
                      value={editForm.production_days}
                      onChange={(e) => setEditForm({...editForm, production_days: parseInt(e.target.value) || 0})}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                      placeholder="e.g. 21"
                      data-testid="production-days-input"
                    />
                    <p className="text-xs text-wt-text-muted mt-1">ETA = production start date + this many days (largest value wins for multi-item orders)</p>
                  </div>
                )}
              </div>

              {/* Express eligibility */}
              <div className="p-4 border border-amber-500/30 rounded-lg bg-amber-500/5" data-testid="express-eligible-section">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label className="text-amber-400 font-medium">Express Eligible</Label>
                    <p className="text-xs text-wt-text-muted mt-1">
                      Orders containing only express-eligible products, shipping to an express warehouse's cities, get flagged EXPRESS with a delivery countdown.
                    </p>
                  </div>
                  <Switch
                    checked={!!editForm.express_eligible}
                    onCheckedChange={(v) => setEditForm({...editForm, express_eligible: v})}
                    data-testid="express-eligible-toggle"
                  />
                </div>
                {editForm.express_eligible && (
                  <div className="mt-3">
                    <Label className="text-wt-text-primary">Express SLA override (hours)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={editForm.express_sla_hours}
                      onChange={(e) => setEditForm({...editForm, express_sla_hours: parseFloat(e.target.value) || 0})}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                      placeholder="0 = use warehouse SLA"
                      data-testid="express-sla-override-input"
                    />
                    <p className="text-xs text-wt-text-muted mt-1">e.g. 2 for 2-hour delivery. 0 uses the warehouse's express time. The fastest item SLA wins per order.</p>
                  </div>
                )}
              </div>

              {/* Virtual Stock — backorder buffer */}
              <div className="p-4 border border-blue-500/30 rounded-lg bg-blue-500/5" data-testid="virtual-stock-section">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label className="text-blue-400 font-medium flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Virtual Stock
                    </Label>
                    <p className="text-xs text-wt-text-muted mt-1">
                      Sell beyond physical stock — backorders auto-fulfill when new inventory arrives.
                    </p>
                  </div>
                  <Switch
                    checked={!!editForm.virtual_enabled}
                    onCheckedChange={(checked) => setEditForm({...editForm, virtual_enabled: checked})}
                    data-testid="virtual-enabled-toggle"
                  />
                </div>
                {editForm.virtual_enabled && (
                  <div className="mt-3 space-y-2">
                    <Label className="text-wt-text-primary text-sm">Virtual stock buffer (max over-sell)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100000"
                      value={editForm.virtual_stock}
                      onChange={(e) => setEditForm({...editForm, virtual_stock: parseInt(e.target.value, 10) || 0})}
                      className="bg-wt-bg border-wt-border text-wt-text-primary"
                      data-testid="virtual-stock-input"
                    />
                    <div className="text-xs text-wt-text-muted">
                      Real {selectedItem?.total_available || selectedItem?.quantity || 0}
                      {' + Virtual '}{editForm.virtual_stock || 0}
                      {' = Sellable '}
                      <span className="text-blue-400 font-semibold">
                        {(selectedItem?.total_available || selectedItem?.quantity || 0) + (parseInt(editForm.virtual_stock, 10) || 0)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Shipping Package */}
              <div className="p-4 border border-purple-500/30 rounded-lg bg-purple-500/5">
                <Label className="text-purple-400 font-medium flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Shipping Package
                </Label>
                <p className="text-xs text-wt-text-muted mt-1 mb-2">
                  Select the package type for courier booking dimensions
                </p>
                <Popover open={packageSearchOpen} onOpenChange={setPackageSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={packageSearchOpen}
                      className="w-full justify-between bg-wt-bg border-wt-border text-wt-text-primary hover:bg-wt-subtle"
                    >
                      {editForm.package_id
                        ? availablePackages.find((pkg) => pkg.id === editForm.package_id)
                          ? `${availablePackages.find((pkg) => pkg.id === editForm.package_id)?.name} (${availablePackages.find((pkg) => pkg.id === editForm.package_id)?.length}×${availablePackages.find((pkg) => pkg.id === editForm.package_id)?.width}×${availablePackages.find((pkg) => pkg.id === editForm.package_id)?.height}cm)`
                          : "Select package..."
                        : "No package assigned"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0 bg-wt-surface border-wt-border" align="start">
                    <Command className="bg-wt-surface">
                      <CommandInput placeholder="Search packages..." className="h-9 text-wt-text-primary" />
                      <CommandList>
                        <CommandEmpty className="text-wt-text-muted py-4 text-center text-sm">No package found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="none"
                            onSelect={() => {
                              setEditForm({...editForm, package_id: null});
                              setPackageSearchOpen(false);
                            }}
                            className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${
                                !editForm.package_id ? "opacity-100" : "opacity-0"
                              }`}
                            />
                            No package assigned
                          </CommandItem>
                          {availablePackages.map((pkg) => (
                            <CommandItem
                              key={pkg.id}
                              value={`${pkg.name} ${pkg.length}x${pkg.width}x${pkg.height}`}
                              onSelect={() => {
                                setEditForm({...editForm, package_id: pkg.id});
                                setPackageSearchOpen(false);
                              }}
                              className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  editForm.package_id === pkg.id ? "opacity-100" : "opacity-0"
                                }`}
                              />
                              <span className="flex-1">
                                {pkg.name} ({pkg.length}×{pkg.width}×{pkg.height}cm, {pkg.weight}{pkg.weight_unit || 'kg'})
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {availablePackages.length === 0 && (
                  <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    No packages created yet.{' '}
                    <a href="/inventory/packages" className="text-wt-gold hover:underline">Create packages</a>
                  </p>
                )}
              </div>
              
              {/* Sell By — Marketplace vendor assignment */}
              <div className="p-4 border border-amber-500/30 rounded-lg bg-amber-500/5" data-testid="edit-sell-by-section">
                <Label className="text-amber-400 font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Sell by
                </Label>
                <p className="text-xs text-wt-text-muted mt-1 mb-3">
                  Choose who supplies this SKU. Marketplace-vendor SKUs auto-mark Shopify selling price = cost × (1 + commission %).
                </p>
                <div className="flex items-center gap-3 mb-3">
                  <label className="inline-flex items-center gap-2 text-sm text-wt-text-primary">
                    <input
                      type="radio"
                      name="sell-by"
                      value="company"
                      checked={(editForm.sell_by || 'company') === 'company'}
                      onChange={() => setEditForm({ ...editForm, sell_by: 'company', marketplace_vendor_id: null, commission_pct_override: '' })}
                      data-testid="edit-sell-by-company"
                    />
                    Company stock
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-wt-text-primary">
                    <input
                      type="radio"
                      name="sell-by"
                      value="vendor"
                      checked={editForm.sell_by === 'vendor'}
                      onChange={() => setEditForm({ ...editForm, sell_by: 'vendor' })}
                      disabled={marketplaceVendors.length === 0}
                      data-testid="edit-sell-by-vendor"
                    />
                    Marketplace vendor
                  </label>
                </div>
                {editForm.sell_by === 'vendor' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-wt-text-primary text-xs">Marketplace vendor</Label>
                      <select
                        value={editForm.marketplace_vendor_id || ''}
                        onChange={(e) => setEditForm({ ...editForm, marketplace_vendor_id: e.target.value || null })}
                        className="mt-1 w-full px-3 py-2 rounded bg-wt-bg border border-wt-border text-wt-text-primary text-sm"
                        data-testid="edit-marketplace-vendor-select"
                      >
                        <option value="">Select a vendor…</option>
                        {marketplaceVendors.map((v) => (
                          <option key={v.id} value={v.id}>{v.vendor_name}</option>
                        ))}
                      </select>
                      {marketplaceVendors.length === 0 && (
                        <p className="text-xs text-amber-400 mt-1">No approved marketplace vendors yet.</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-wt-text-primary text-xs">Commission % override (optional)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        placeholder="Use vendor default"
                        value={editForm.commission_pct_override}
                        onChange={(e) => setEditForm({ ...editForm, commission_pct_override: e.target.value })}
                        className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                        data-testid="edit-commission-override"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Sync to Shopify Toggle */}
              {selectedItem.shopify_variant_id && (
                <div className="flex items-center justify-between p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 text-blue-400" />
                    <div>
                      <p className="text-wt-text-primary text-sm font-medium">Sync cost to Shopify</p>
                      <p className="text-xs text-wt-text-muted">Update the cost price in Shopify inventory</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={syncToShopify}
                      onChange={(e) => setSyncToShopify(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-wt-subtle rounded-full peer peer-checked:bg-blue-500 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                  </label>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="ghost" onClick={() => setEditDialogOpen(false)} className="text-wt-text-secondary">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveEdit}
                  disabled={savingEdit}
                  className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                  data-testid="save-edit-btn"
                >
                  {savingEdit ? (
                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vendor Mapping Dialog */}
      <Dialog open={vendorMappingDialogOpen} onOpenChange={(open) => {
        setVendorMappingDialogOpen(open);
        if (!open) setSelectedItem(null);
      }}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Vendor Mapping</DialogTitle>
          </DialogHeader>
          {selectedItem ? (
            <div className="space-y-4">
              <div className="p-3 bg-wt-subtle rounded-lg">
                <p className="text-wt-text-primary font-medium">{selectedItem.product_title || selectedItem.name || 'Unknown Product'}</p>
                <p className="text-sm text-wt-text-muted">SKU: {selectedItem.sku || 'N/A'}</p>
              </div>
              
              <Tabs defaultValue="primary" className="w-full">
                <TabsList className="bg-wt-subtle">
                  <TabsTrigger value="primary">Primary Vendor</TabsTrigger>
                  <TabsTrigger value="backup">Backup Vendors</TabsTrigger>
                  <TabsTrigger value="comparison">Price Comparison</TabsTrigger>
                </TabsList>
                
                <TabsContent value="primary" className="space-y-4 mt-4">
                  <div>
                    <Label className="text-wt-text-primary">Select Primary Vendor</Label>
                    <Select 
                      value={vendorMappingForm.vendor_id} 
                      onValueChange={(value) => setVendorMappingForm({...vendorMappingForm, vendor_id: value})}
                    >
                      <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue placeholder="Choose vendor..." />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-paper border-wt-border">
                        {(vendors || []).map(v => (
                          <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-wt-text-primary">Vendor SKU</Label>
                      <Input 
                        value={vendorMappingForm.vendor_sku}
                        onChange={(e) => setVendorMappingForm({...vendorMappingForm, vendor_sku: e.target.value})}
                        placeholder={selectedItem.sku}
                        className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" 
                      />
                    </div>
                    <div>
                      <Label className="text-wt-text-primary">Cost Price</Label>
                      <Input 
                        type="number" 
                        value={vendorMappingForm.cost_price}
                        onChange={(e) => setVendorMappingForm({...vendorMappingForm, cost_price: e.target.value})}
                        placeholder="0.00"
                        className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" 
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-wt-text-primary">MOQ</Label>
                      <Input 
                        type="number" 
                        value={vendorMappingForm.moq}
                        onChange={(e) => setVendorMappingForm({...vendorMappingForm, moq: e.target.value})}
                        className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" 
                      />
                    </div>
                    <div>
                      <Label className="text-wt-text-primary">Lead Time (days)</Label>
                      <Input 
                        type="number" 
                        value={vendorMappingForm.lead_time_days}
                        onChange={(e) => setVendorMappingForm({...vendorMappingForm, lead_time_days: e.target.value})}
                        className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" 
                      />
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="backup" className="mt-4">
                  <p className="text-wt-text-muted text-center py-8">No backup vendors configured</p>
                  <Button variant="outline" className="w-full border-wt-border text-wt-text-primary">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Backup Vendor
                  </Button>
                </TabsContent>
                
                <TabsContent value="comparison" className="mt-4">
                  <p className="text-wt-text-muted text-center py-8">No vendor price data available</p>
                </TabsContent>
              </Tabs>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-wt-border">
                <Button variant="ghost" onClick={() => setVendorMappingDialogOpen(false)} className="text-wt-text-secondary">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveVendorMapping}
                  disabled={savingMapping}
                  className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                >
                  {savingMapping ? 'Saving...' : 'Save Mapping'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-wt-text-muted">
              <p>No item selected</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Dialog */}
      <Dialog open={adjustStockDialogOpen} onOpenChange={setAdjustStockDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="adjust-stock-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <PlusCircle className="h-5 w-5 text-green-400" />
              Adjust Stock
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-4 pt-4">
              {/* Item Info */}
              <div className="p-3 bg-wt-subtle rounded-lg">
                <p className="text-sm text-wt-text-muted">Product</p>
                <p className="text-wt-text-primary font-medium">{selectedItem.product_title}</p>
                {selectedItem.variant_title && (
                  <p className="text-sm text-wt-text-muted">{selectedItem.variant_title}</p>
                )}
                <p className="text-wt-gold font-mono text-sm mt-1">SKU: {selectedItem.sku}</p>
              </div>
              
              {/* Location-based Inventory (if available) */}
              {loadingLocationInventory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 text-wt-gold animate-spin" />
                  <span className="ml-2 text-wt-text-muted">Loading inventory levels...</span>
                </div>
              ) : locationInventory.length > 0 ? (
                <div className="border border-wt-border rounded-lg overflow-hidden">
                  {/* Header */}
                  <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-wt-subtle text-xs font-semibold text-wt-text-primary uppercase">
                    <div className="col-span-5">Location</div>
                    <div className="col-span-2 text-center">Available</div>
                    <div className="col-span-2 text-center">On Hand</div>
                    <div className="col-span-3 text-center">Update</div>
                  </div>
                  
                  {/* Location Rows */}
                  {locationInventory.map((loc) => (
                    <LocationInventoryRow
                      key={loc.location_id}
                      location={loc}
                      isSelected={selectedLocationId === loc.location_id}
                      onSelect={() => setSelectedLocationId(loc.location_id)}
                      onSetLevel={(newLevel) => handleSetStockLevel(loc.location_id, newLevel)}
                      disabled={adjustingStock}
                    />
                  ))}
                  
                  {/* Total Row */}
                  <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-wt-subtle border-t border-wt-border font-semibold">
                    <div className="col-span-5 text-wt-text-primary">Total</div>
                    <div className="col-span-2 text-center text-green-400">
                      {locationInventory.reduce((sum, l) => sum + (l.available || 0), 0)}
                    </div>
                    <div className="col-span-2 text-center text-wt-text-primary">
                      {locationInventory.reduce((sum, l) => sum + (l.on_hand || 0), 0)}
                    </div>
                    <div className="col-span-3"></div>
                  </div>

                  {/* Ghost stock warning: app master total ≠ Shopify location total */}
                  {(() => {
                    const locTotal = locationInventory.reduce((sum, l) => sum + (l.available || 0), 0);
                    const appTotal = selectedItem.total_available || 0;
                    if (locTotal === appTotal) return null;
                    return (
                      <div className="px-4 py-3 bg-amber-500/10 border-t border-amber-500/30 text-xs text-amber-400" data-testid="ghost-stock-warning">
                        <AlertTriangle className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />
                        App total stock is <strong>{appTotal}</strong> but locations hold <strong>{locTotal}</strong>.
                        To fix this, choose <strong>Set Exact Total (Correction)</strong> below and enter the real quantity (e.g. {locTotal}).
                      </div>
                    );
                  })()}
                </div>
              ) : (
                /* Fallback: Simple inventory view with location breakdown */
                <div className="space-y-3">
                  <div className="flex items-center gap-4 p-3 bg-wt-subtle rounded-lg">
                    <span className="text-sm text-wt-text-muted">Total Stock:</span>
                    <span className="text-lg font-bold text-wt-text-primary">{selectedItem.total_available || 0}</span>
                  </div>
                  {(() => {
                    const locTotal = (selectedItem.locations || []).reduce((s, l) => s + (parseInt(l.quantity ?? l.available) || 0), 0);
                    const appTotal = selectedItem.total_available || 0;
                    if (locTotal === appTotal) return null;
                    return (
                      <div className="px-3 py-2.5 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs text-amber-400" data-testid="ghost-stock-warning">
                        <AlertTriangle className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />
                        Total stock is <strong>{appTotal}</strong> but locations hold <strong>{locTotal}</strong>.
                        To fix this, choose <strong>Set Exact Total (Correction)</strong> below and enter the real quantity (e.g. {locTotal}).
                      </div>
                    );
                  })()}
                  
                  {/* Location-wise breakup */}
                  <div className="border border-wt-border rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-wt-subtle border-b border-wt-border flex items-center justify-between">
                      <h4 className="text-xs font-semibold text-wt-text-primary uppercase flex items-center gap-2">
                        <MapPin className="h-3 w-3 text-wt-gold" />
                        Stock by Location
                      </h4>
                      <button
                        className="text-[11px] text-wt-gold hover:underline"
                        data-testid="push-levels-to-shopify-btn"
                        onClick={async () => {
                          try {
                            const r = await axios.post(`${API_BASE_URL}/enterprise/inventory/${encodeURIComponent(selectedItem.sku)}/push-levels-to-shopify`, {}, getAuthHeaders());
                            toast.success(`Pushed to Shopify: ${r.data.pushed} location(s)${r.data.failed?.length ? `, ${r.data.failed.length} failed (${r.data.failed[0]?.error || ''})` : ''}`);
                            openAdjustStockDialog(selectedItem);
                          } catch (e) { toast.error(e.response?.data?.detail || 'Push failed'); }
                        }}
                      >Push to Shopify</button>
                    </div>
                    <div className="divide-y divide-wt-border/50">
                      {locations.map(loc => {
                        const locStock = selectedItem.locations?.find(l => 
                          String(l.location_id) === String(loc.id) || 
                          (loc.shopify_location_id && String(l.location_id) === String(loc.shopify_location_id)) ||
                          String(l.shopify_location_id) === String(loc.id) ||
                          (l.location_name || l.name) === loc.name
                        );
                        const qty = locStock?.quantity ?? locStock?.available ?? 0;
                        return (
                          <div key={loc.id} className="flex justify-between items-center px-3 py-2 text-sm">
                            <span className="text-wt-text-muted">{loc.name}</span>
                            <span className={`font-medium ${qty > 0 ? 'text-green-400' : 'text-wt-text-muted'}`}>{qty}</span>
                          </div>
                        );
                      })}
                      {/* Buckets holding stock that didn't match any warehouse row — never hide stock */}
                      {(selectedItem.locations || []).filter(l => {
                        const q = parseInt(l.quantity ?? l.available) || 0;
                        if (q === 0) return false;
                        return !locations.some(loc =>
                          String(l.location_id) === String(loc.id) ||
                          (loc.shopify_location_id && String(l.location_id) === String(loc.shopify_location_id)) ||
                          String(l.shopify_location_id) === String(loc.id) ||
                          (l.location_name || l.name) === loc.name
                        );
                      }).map((l, i) => (
                        <div key={`unmatched-${i}`} className="flex justify-between items-center px-3 py-2 text-sm" data-testid="unmatched-location-row">
                          <span className="text-amber-400">{l.location_name || l.name || `Location ${l.location_id}`}</span>
                          <span className="font-medium text-green-400">{parseInt(l.quantity ?? l.available) || 0}</span>
                        </div>
                      ))}
                      {locations.length === 0 && (
                        <div className="px-3 py-2 text-sm text-wt-text-muted">No locations configured</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Quick Adjustment Section */}
              <div className="border-t border-wt-border pt-4">
                <h4 className="text-sm font-semibold text-wt-text-primary mb-3 flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Quick Adjustment
                  {locationInventory.length > 0 && selectedLocationId && (
                    <span className="text-xs text-wt-gold font-normal">
                      (at {locationInventory.find(l => l.location_id === selectedLocationId)?.location_name || 'Selected Location'})
                    </span>
                  )}
                </h4>
                
                {/* Location Selection (if multiple locations) */}
                {locationInventory.length > 1 && (
                  <div className="mb-3">
                    <Label className="text-wt-text-muted text-xs">Location</Label>
                    <Select 
                      value={selectedLocationId} 
                      onValueChange={(value) => {
                        setSelectedLocationId(value);
                        setSelectedRackNumber('');
                        // Load racks for the selected location
                        if (selectedItem?.sku && value) {
                          loadRacksForAdjustment(selectedItem.sku, value, adjustmentType);
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-paper border-wt-border">
                        {locationInventory.filter(l => l.active).map((loc) => (
                          <SelectItem key={loc.location_id} value={loc.location_id}>
                            <span className="flex items-center gap-2">
                              <MapPin className="h-3 w-3 text-wt-gold" />
                              {loc.location_name}
                              <span className="text-wt-text-muted">({loc.available} available)</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Location Selection for fallback (when no Shopify location data) */}
                {locationInventory.length === 0 && locations.length > 0 && (
                  <div className="mb-3">
                    <Label className="text-wt-text-muted text-xs">Location</Label>
                    <Select 
                      value={selectedLocationId} 
                      onValueChange={(value) => {
                        setSelectedLocationId(value);
                        setSelectedRackNumber('');
                        // Load racks for the selected location
                        if (selectedItem?.sku && value) {
                          loadRacksForAdjustment(selectedItem.sku, value, adjustmentType);
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-paper border-wt-border">
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={String(loc.id)}>
                            <span className="flex items-center gap-2">
                              <MapPin className="h-3 w-3 text-wt-gold" />
                              {loc.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Smart Rack Selection - shows filtered racks based on adjustment type */}
                {(availableRacksForAdjustment.length > 0 || racks.length > 0) && (
                  <div className="mb-3">
                    <Label className="text-wt-text-muted text-xs">
                      Rack {adjustmentType === 'remove' && <span className="text-orange-400">(only racks with stock)</span>}
                    </Label>
                    <Select value={selectedRackNumber || 'default'} onValueChange={setSelectedRackNumber}>
                      <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue placeholder="Select rack" />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-paper border-wt-border max-h-48">
                        <SelectItem value="default">
                          <span className="text-wt-text-primary">Default</span>
                          <span className="text-wt-text-muted text-xs ml-2">(No specific rack)</span>
                        </SelectItem>
                        {adjustmentType === 'remove' ? (
                          // For Remove: show only racks with inventory (from rack_inventory response)
                          availableRacksForAdjustment.length > 0 ? (
                            availableRacksForAdjustment.map((rack) => (
                              <SelectItem key={rack.rack_number} value={rack.rack_number}>
                                <span className="flex items-center gap-2 font-mono">
                                  {rack.rack_number}
                                  <span className="text-green-400 text-xs">(Qty: {rack.quantity})</span>
                                </span>
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="no-stock" disabled>
                              <span className="text-wt-text-muted text-xs">No racks with stock</span>
                            </SelectItem>
                          )
                        ) : (
                          // For Add: show all available racks
                          (availableRacksForAdjustment.length > 0 ? availableRacksForAdjustment : racks).map((rack) => (
                            <SelectItem key={rack.rack_number || rack.id} value={rack.rack_number}>
                              <span className="flex items-center gap-2 font-mono">
                                {rack.rack_number}
                                {rack.quantity !== undefined && (
                                  <span className="text-wt-text-muted text-xs">(Current: {rack.quantity})</span>
                                )}
                              </span>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Show Default rack option even if no racks configured */}
                {availableRacksForAdjustment.length === 0 && racks.length === 0 && (
                  <div className="mb-3">
                    <Label className="text-wt-text-muted text-xs">Rack</Label>
                    <Select value="default" disabled>
                      <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue>Default (No racks configured)</SelectValue>
                      </SelectTrigger>
                    </Select>
                  </div>
                )}
              
                <div className="grid grid-cols-2 gap-3">
                  {/* Adjustment Type */}
                  <div>
                    <Label className="text-wt-text-muted text-xs">Type</Label>
                    <Select 
                      value={adjustmentType} 
                      onValueChange={(value) => {
                        setAdjustmentType(value);
                        setSelectedRackNumber('');
                        // Load appropriate racks based on adjustment type
                        if (selectedItem?.sku) {
                          loadRacksForAdjustment(selectedItem.sku, selectedLocationId, value);
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-paper border-wt-border">
                        <SelectItem value="add">
                          <span className="flex items-center gap-2">
                            <PlusCircle className="h-4 w-4 text-green-400" />
                            Add Stock
                          </span>
                        </SelectItem>
                        <SelectItem value="remove">
                          <span className="flex items-center gap-2">
                            <MinusCircle className="h-4 w-4 text-red-400" />
                            Remove Stock
                          </span>
                        </SelectItem>
                        <SelectItem value="set" data-testid="adjust-type-set-exact">
                          <span className="flex items-center gap-2">
                            <Equal className="h-4 w-4 text-amber-400" />
                            Set Exact Total (Correction)
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Quantity */}
                  <div>
                    <Label className="text-wt-text-muted text-xs">Quantity</Label>
                    <Input
                      type="number"
                      min={adjustmentType === 'set' ? '0' : '1'}
                      value={adjustmentQuantity}
                      onChange={(e) => setAdjustmentQuantity(e.target.value)}
                      placeholder={adjustmentType === 'set' ? 'Exact total (e.g. 0)' : 'Enter qty'}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                      data-testid="adjust-quantity-input"
                    />
                  </div>
                </div>
                
                {/* Reason */}
                <div className="mt-3">
                  <Label className="text-wt-text-muted text-xs">Reason (Optional)</Label>
                  <Input
                    value={adjustmentReason}
                    onChange={(e) => setAdjustmentReason(e.target.value)}
                    placeholder="e.g., Stock received, Damaged goods, Cycle count"
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                
                {/* Preview */}
                {adjustmentQuantity !== '' && !isNaN(parseInt(adjustmentQuantity)) && (adjustmentType === 'set' ? parseInt(adjustmentQuantity) >= 0 : parseInt(adjustmentQuantity) > 0) && (
                  <div className="mt-3 p-3 bg-wt-subtle rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-wt-text-muted">{adjustmentType === 'set' ? 'New Total Stock:' : 'New Stock Level:'}</span>
                      <span className={`text-lg font-bold ${adjustmentType === 'add' ? 'text-green-400' : adjustmentType === 'set' ? 'text-amber-400' : 'text-red-400'}`} data-testid="adjust-preview-value">
                        {(() => {
                          if (adjustmentType === 'set') return parseInt(adjustmentQuantity);
                          const currentQty = locationInventory.length > 0 && selectedLocationId
                            ? (locationInventory.find(l => l.location_id === selectedLocationId)?.available || 0)
                            : (selectedItem.total_available || 0);
                          return currentQty + (adjustmentType === 'add' ? parseInt(adjustmentQuantity) : -parseInt(adjustmentQuantity));
                        })()}
                      </span>
                    </div>
                    {adjustmentType === 'set' && (
                      <p className="text-xs text-wt-text-muted mt-2">
                        Sets the app's master total to exactly this value (ledger + channels updated). Use this to clear wrong/ghost stock.
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2 border-t border-wt-border">
                <Button 
                  variant="ghost" 
                  onClick={() => setAdjustStockDialogOpen(false)} 
                  className="text-wt-text-secondary"
                  disabled={adjustingStock}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleAdjustStock}
                  disabled={adjustingStock || adjustmentQuantity === '' || isNaN(parseInt(adjustmentQuantity)) || (adjustmentType === 'set' ? parseInt(adjustmentQuantity) < 0 : parseInt(adjustmentQuantity) <= 0)}
                  className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                  data-testid="confirm-adjust-stock"
                >
                  {adjustingStock ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adjusting...
                    </>
                  ) : adjustmentType === 'set' ? (
                    <>
                      <Equal className="h-4 w-4 mr-2" />
                      Set Exact Total
                    </>
                  ) : (
                    <>
                      {adjustmentType === 'add' ? <PlusCircle className="h-4 w-4 mr-2" /> : <MinusCircle className="h-4 w-4 mr-2" />}
                      {adjustmentType === 'add' ? 'Add' : 'Remove'} Stock
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Inventory Item Dialog */}
      <Dialog open={addItemDialogOpen} onOpenChange={setAddItemDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md" data-testid="add-inventory-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Plus className="h-5 w-5 text-wt-gold" />
              Add Inventory Item
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* SKU */}
            <div>
              <Label className="text-wt-text-primary">SKU *</Label>
              <Input
                value={newItemForm.sku}
                onChange={(e) => setNewItemForm({...newItemForm, sku: e.target.value})}
                placeholder="Enter unique SKU"
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                data-testid="new-item-sku"
              />
            </div>
            
            {/* Product Title */}
            <div>
              <Label className="text-wt-text-primary">Product Title *</Label>
              <Input
                value={newItemForm.product_title}
                onChange={(e) => setNewItemForm({...newItemForm, product_title: e.target.value})}
                placeholder="Enter product name"
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                data-testid="new-item-title"
              />
            </div>
            
            {/* Variant Title */}
            <div>
              <Label className="text-wt-text-primary">Variant (Optional)</Label>
              <Input
                value={newItemForm.variant_title}
                onChange={(e) => setNewItemForm({...newItemForm, variant_title: e.target.value})}
                placeholder="e.g., Large / Blue"
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            
            {/* Quantity and Cost */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Initial Stock</Label>
                <Input
                  type="number"
                  min="0"
                  value={newItemForm.total_available}
                  onChange={(e) => setNewItemForm({...newItemForm, total_available: parseInt(e.target.value) || 0})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  data-testid="new-item-quantity"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary">Unit Cost</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newItemForm.unit_cost}
                  onChange={(e) => setNewItemForm({...newItemForm, unit_cost: parseFloat(e.target.value) || 0})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>
            
            {/* Min Stock Level */}
            <div>
              <Label className="text-wt-text-primary">Minimum Stock Level</Label>
              <Input
                type="number"
                min="0"
                value={newItemForm.min_stock_level}
                onChange={(e) => setNewItemForm({...newItemForm, min_stock_level: parseInt(e.target.value) || 5})}
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button 
                variant="ghost" 
                onClick={() => setAddItemDialogOpen(false)} 
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddItem}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                data-testid="confirm-add-item"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjustment History Dialog */}
      {/* Cost-Change History Modal (May 2026) */}
      <Dialog open={costHistoryDialogOpen} onOpenChange={setCostHistoryDialogOpen}>
        <DialogContent
          className="bg-wt-paper border-wt-border max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
          data-testid="cost-history-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-amber-400" />
              Cost Change History
            </DialogTitle>
          </DialogHeader>

          {costHistoryItem && (
            <div className="mb-4 p-3 bg-wt-subtle rounded-lg">
              <p className="text-wt-text-primary font-medium">
                {costHistoryItem.product_title || costHistoryItem.name}
              </p>
              <p className="text-sm text-wt-text-muted font-mono">{costHistoryItem.sku}</p>
            </div>
          )}

          {costHistoryData && costHistoryData.total_changes > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3 bg-wt-subtle rounded-lg">
                <div className="text-[11px] uppercase tracking-wide text-wt-text-muted">Latest Cost</div>
                <div className="text-xl font-semibold text-wt-text-primary">
                  {(costHistoryData.latest_cost || 0).toFixed(2)}
                </div>
              </div>
              <div className="p-3 bg-wt-subtle rounded-lg">
                <div className="text-[11px] uppercase tracking-wide text-wt-text-muted">First Recorded</div>
                <div className="text-xl font-semibold text-wt-text-primary">
                  {(costHistoryData.first_recorded_cost || 0).toFixed(2)}
                </div>
              </div>
              <div className="p-3 bg-wt-subtle rounded-lg">
                <div className="text-[11px] uppercase tracking-wide text-wt-text-muted">Δ Last 30 Days</div>
                <div className={`text-xl font-semibold ${
                  (costHistoryData.delta_last_30_days || 0) > 0
                    ? 'text-amber-300'
                    : (costHistoryData.delta_last_30_days || 0) < 0
                    ? 'text-emerald-300'
                    : 'text-wt-text-primary'
                }`}>
                  {(costHistoryData.delta_last_30_days || 0) > 0 ? '+' : ''}
                  {(costHistoryData.delta_last_30_days || 0).toFixed(2)}
                </div>
              </div>
            </div>
          )}

          <div className="overflow-auto flex-1">
            {loadingCostHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-wt-text-muted" />
              </div>
            ) : !costHistoryData || (costHistoryData.rows || []).length === 0 ? (
              <div className="text-center py-12 text-wt-text-muted text-sm">
                No cost changes recorded for this SKU yet.
                <br />
                Cost-history rows are written when a PO is processed or an admin
                edits the cost. Ask a super-admin to run the historical
                backfill to import past purchase orders.
              </div>
            ) : (
              <div className="space-y-2" data-testid="cost-history-list">
                {(costHistoryData.rows || []).map((row, idx) => {
                  const delta = (row.new_cost || 0) - (row.previous_cost || 0);
                  return (
                    <div
                      key={row.id || `${row.created_at}-${idx}`}
                      className="bg-wt-subtle/40 border border-wt-border rounded-lg p-3"
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${
                            delta > 0
                              ? 'bg-amber-500/15 text-amber-300'
                              : delta < 0
                              ? 'bg-emerald-500/15 text-emerald-300'
                              : 'bg-slate-500/15 text-slate-300'
                          }`}>
                            {delta > 0 ? '↑' : delta < 0 ? '↓' : '='}
                            {Math.abs(delta).toFixed(2)}
                          </span>
                          <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                            row.source === 'po'
                              ? 'bg-blue-500/15 text-blue-300'
                              : row.source === 'manual'
                              ? 'bg-wt-gold/15 text-wt-gold'
                              : row.source === 'backfill'
                              ? 'bg-slate-500/15 text-slate-300'
                              : 'bg-violet-500/15 text-violet-300'
                          }`}>
                            {row.source}
                          </span>
                          {row.po_number && (
                            <code className="text-[11px] text-wt-text-muted font-mono">
                              {row.po_number}
                            </code>
                          )}
                          {row.vendor_name && (
                            <span className="text-[12px] text-wt-text-primary truncate max-w-xs" title={row.vendor_name}>
                              {row.vendor_name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-wt-text-muted shrink-0">
                          <span className="text-rose-300">{(row.previous_cost || 0).toFixed(2)}</span>
                          <span>→</span>
                          <span className="text-emerald-300">{(row.new_cost || 0).toFixed(2)}</span>
                          <span className="text-wt-text-muted/70 ml-1">{row.currency || 'AED'}</span>
                        </div>
                      </div>
                      <div className="mt-1.5 flex items-center gap-3 text-[11px] text-wt-text-muted flex-wrap">
                        {row.quantity_received != null && (
                          <span>qty {row.quantity_received}</span>
                        )}
                        {row.user_name && <span>by {row.user_name}</span>}
                        <span className="ml-auto">
                          {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>


      <Dialog open={adjustmentHistoryDialogOpen} onOpenChange={setAdjustmentHistoryDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl flex items-center gap-2">
              <History className="h-6 w-6 text-wt-gold" />
              Stock Adjustment History
            </DialogTitle>
          </DialogHeader>
          
          {adjustmentHistoryItem && (
            <div className="mb-4 p-3 bg-wt-subtle rounded-lg">
              <p className="text-wt-text-primary font-medium">
                {adjustmentHistoryItem.product_title || adjustmentHistoryItem.name}
              </p>
              <p className="text-sm text-wt-text-muted font-mono">{adjustmentHistoryItem.sku}</p>
            </div>
          )}
          
          {loadingHistory ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-wt-gold animate-spin" />
            </div>
          ) : adjustmentHistory.length === 0 ? (
            <div className="text-center py-12 text-wt-text-muted">
              <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No stock adjustments recorded for this product</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-wt-subtle sticky top-0">
                  <tr>
                    <th className="text-left p-3 text-wt-text-muted">Date & Time</th>
                    <th className="text-left p-3 text-wt-text-muted">Location / Rack</th>
                    <th className="text-left p-3 text-wt-text-muted">Reason</th>
                    <th className="text-center p-3 text-wt-text-muted">Change</th>
                    <th className="text-center p-3 text-wt-text-muted">Stock</th>
                    <th className="text-left p-3 text-wt-text-muted">User</th>
                  </tr>
                </thead>
                <tbody>
                  {adjustmentHistory.map((adj) => (
                    <tr key={adj.id} className="border-b border-wt-border/50">
                      <td className="p-3 text-wt-text-primary text-xs">
                        {new Date(adj.created_at).toLocaleString()}
                      </td>
                      <td className="p-3">
                        <div>
                          {adj.location_name && (
                            <span className="text-wt-text-primary text-xs flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-wt-gold" />
                              {adj.location_name}
                            </span>
                          )}
                          <span className="text-xs text-blue-400 font-mono block mt-0.5">
                            Rack: {adj.rack_number || 'Default'}
                          </span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div>
                          <span className="text-wt-text-primary">{formatAdjustmentReason(adj.reason)}</span>
                          {adj.reason_details && (
                            <p className="text-xs text-wt-text-muted">{adj.reason_details}</p>
                          )}
                          {adj.reference_id && (
                            <p className="text-xs text-blue-400">Ref: {adj.reference_id.slice(0,8)}...</p>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`font-mono font-semibold ${adj.change_quantity >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {adj.change_quantity >= 0 ? '+' : ''}{adj.change_quantity}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-wt-text-muted">{adj.previous_quantity}</span>
                        <span className="text-wt-text-muted mx-1">→</span>
                        <span className="text-wt-text-primary font-medium">{adj.new_quantity}</span>
                      </td>
                      <td className="p-3 text-wt-text-secondary text-xs">
                        {adj.user_name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <div className="flex justify-end pt-4 border-t border-wt-border">
            <Button 
              variant="ghost" 
              onClick={() => setAdjustmentHistoryDialogOpen(false)}
              className="text-wt-text-secondary"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rack-wise Inventory Breakdown Dialog */}
      <Dialog open={rackInventoryDialogOpen} onOpenChange={setRackInventoryDialogOpen}>
        <DialogContent className="bg-wt-surface border border-wt-border text-wt-text-primary max-w-lg" data-testid="rack-inventory-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-gold flex items-center gap-2">
              <Boxes className="h-5 w-5" />
              Rack-wise Inventory
            </DialogTitle>
          </DialogHeader>
          
          {loadingRackInventory ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
            </div>
          ) : rackInventoryData ? (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="bg-wt-subtle p-3 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-wt-text-primary">{selectedItem?.product_title}</p>
                    <p className="text-xs text-wt-text-muted font-mono">{rackInventoryData.sku}</p>
                  </div>
                  {rackInventoryLocation?.name && (
                    <Badge className="bg-wt-gold/20 text-wt-gold">
                      <MapPin className="h-3 w-3 mr-1" />
                      {rackInventoryLocation.name}
                    </Badge>
                  )}
                </div>
                <div className="mt-2 pt-2 border-t border-wt-border/50 flex flex-wrap items-center gap-3">
                  <div>
                    <span className="text-sm text-wt-text-muted">Total: </span>
                    <span className="text-lg font-bold text-green-400" data-testid="rack-total-qty">{rackInventoryData.total_quantity || 0}</span>
                  </div>
                  {rackInventoryData.total_blocked > 0 && (
                    <>
                      <div>
                        <span className="text-sm text-wt-text-muted">Blocked: </span>
                        <button
                          type="button"
                          className="text-base font-semibold text-amber-400 hover:text-amber-300 underline decoration-dotted underline-offset-2"
                          data-testid="rack-total-blocked"
                          title="Click to see which orders are blocking this stock"
                          onClick={() => loadBlockedOrders(rackInventoryData.sku, rackInventoryLocation?.id || null)}
                        >
                          {rackInventoryData.total_blocked}
                        </button>
                      </div>
                      <div>
                        <span className="text-sm text-wt-text-muted">Sellable: </span>
                        <span className="text-base font-semibold text-blue-400" data-testid="rack-total-sellable">
                          {rackInventoryData.total_sellable ?? rackInventoryData.total_quantity}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Rack Breakdown Table */}
              {rackInventoryData.rack_inventory?.length > 0 ? (
                <div className="border border-wt-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-wt-bg">
                      <tr>
                        <th className="text-left p-3 text-wt-text-muted font-medium">Rack</th>
                        <th className="text-center p-3 text-wt-text-muted font-medium">Quantity</th>
                        <th className="text-center p-3 text-wt-text-muted font-medium">Blocked</th>
                        <th className="text-center p-3 text-wt-text-muted font-medium">Sellable</th>
                        <th className="text-right p-3 text-wt-text-muted font-medium">Last Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rackInventoryData.rack_inventory.map((rack, idx) => (
                        <tr key={idx} className="border-t border-wt-border hover:bg-wt-subtle/50" data-testid={`rack-row-${rack.rack_number}`}>
                          <td className="p-3">
                            <span className="font-mono text-wt-text-primary font-medium">
                              {rack.rack_number}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`font-semibold ${rack.quantity > 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {rack.quantity}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            {rack.blocked_quantity > 0 ? (
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 font-semibold hover:bg-amber-500/25 cursor-pointer"
                                title={(Object.entries(rack.blocked_by_channel || {})
                                  .map(([ch, q]) => `${ch}: ${q}`).join(', ') || 'Reserved') + ' — click to see orders'}
                                data-testid={`rack-blocked-${rack.rack_number}`}
                                onClick={() => loadBlockedOrders(rackInventoryData.sku, rack.location_id || rackInventoryLocation?.id || null)}
                              >
                                🔒 {rack.blocked_quantity}
                              </button>
                            ) : (
                              <span className="text-wt-text-muted">—</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <span className="font-semibold text-blue-400">
                              {rack.sellable_quantity ?? rack.quantity}
                            </span>
                          </td>
                          <td className="p-3 text-right text-wt-text-muted text-xs">
                            {rack.last_adjustment ? new Date(rack.last_adjustment).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6 bg-wt-subtle rounded-lg">
                  <Boxes className="h-10 w-10 text-wt-text-muted mx-auto mb-2" />
                  <p className="text-wt-text-muted">No rack-specific inventory found</p>
                  <p className="text-xs text-wt-text-muted mt-1">Stock may be in default location</p>
                </div>
              )}
              
              {/* Quick Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 border-wt-border text-wt-text-primary hover:bg-wt-subtle"
                  onClick={() => {
                    setRackInventoryDialogOpen(false);
                    if (selectedItem) {
                      setSelectedLocationId(rackInventoryLocation?.id || '');
                      setAdjustStockDialogOpen(true);
                    }
                  }}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Adjust Stock
                </Button>
                <Button
                  variant="ghost"
                  className="text-wt-text-secondary"
                  onClick={() => setRackInventoryDialogOpen(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-wt-text-muted">No data available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Blocked Orders Drill-Down Dialog ────────────────────────────── */}
      <Dialog open={blockedOrdersDialogOpen} onOpenChange={setBlockedOrdersDialogOpen}>
        <DialogContent
          className="bg-wt-surface border border-wt-border text-wt-text-primary max-w-2xl max-h-[80vh] overflow-y-auto"
          data-testid="blocked-orders-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-wt-gold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              Orders blocking stock — <span className="font-mono">{blockedOrdersSku}</span>
            </DialogTitle>
          </DialogHeader>

          {loadingBlockedOrders ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
            </div>
          ) : blockedOrdersData && (blockedOrdersData.blocked_orders || []).length > 0 ? (
            <div className="space-y-3">
              <div className="bg-wt-subtle p-3 rounded-lg text-sm flex items-center justify-between">
                <span className="text-wt-text-muted">
                  {blockedOrdersData.count} reservation{blockedOrdersData.count === 1 ? '' : 's'} ·
                  {' '}
                  <span className="text-amber-400 font-semibold">{blockedOrdersData.total_blocked}</span>
                  {' '}unit{blockedOrdersData.total_blocked === 1 ? '' : 's'} reserved
                </span>
                <span className="text-xs text-wt-text-muted">Newest first</span>
              </div>

              <div className="border border-wt-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-wt-bg">
                    <tr>
                      <th className="text-left p-3 text-wt-text-muted font-medium">Order</th>
                      <th className="text-left p-3 text-wt-text-muted font-medium">Channel</th>
                      <th className="text-left p-3 text-wt-text-muted font-medium">Customer</th>
                      <th className="text-center p-3 text-wt-text-muted font-medium">Qty</th>
                      <th className="text-left p-3 text-wt-text-muted font-medium">Rack</th>
                      <th className="text-right p-3 text-wt-text-muted font-medium">Reserved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blockedOrdersData.blocked_orders.map((bo) => {
                      const orderLabel =
                        bo.order?.shopify_order_name
                        || bo.order?.order_number
                        || bo.channel_order_id
                        || '—';
                      const opsId = bo.opswarex_order_id || bo.order?.id;
                      const customerName = bo.order?.customer_name || '—';
                      const reservedAt = bo.reserved_at
                        ? new Date(bo.reserved_at).toLocaleString()
                        : '—';
                      return (
                        <tr
                          key={bo.reservation_id}
                          className="border-t border-wt-border hover:bg-wt-subtle/50"
                          data-testid={`blocked-order-row-${bo.reservation_id}`}
                        >
                          <td className="p-3">
                            {opsId ? (
                              <a
                                href={`/orders?id=${opsId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-wt-gold hover:underline inline-flex items-center gap-1 font-mono"
                                data-testid={`blocked-order-link-${bo.reservation_id}`}
                              >
                                {orderLabel}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : (
                              <span className="font-mono text-wt-text-primary">{orderLabel}</span>
                            )}
                            {bo.is_stockout && (
                              <Badge className="ml-2 bg-red-500/15 text-red-400 border border-red-500/40 text-[10px]">
                                STOCKOUT
                              </Badge>
                            )}
                          </td>
                          <td className="p-3">
                            <Badge className="bg-wt-subtle text-wt-text-secondary border border-wt-border">
                              {bo.channel_type || 'unknown'}
                            </Badge>
                          </td>
                          <td className="p-3 text-wt-text-secondary">
                            {customerName}
                          </td>
                          <td className="p-3 text-center">
                            <span className="font-semibold text-amber-400">{bo.quantity}</span>
                          </td>
                          <td className="p-3 text-wt-text-secondary font-mono text-xs">
                            {bo.rack_number || 'Default'}
                            {bo.location_name ? (
                              <span className="block text-[10px] text-wt-text-muted">
                                {bo.location_name}
                              </span>
                            ) : null}
                          </td>
                          <td className="p-3 text-right text-xs text-wt-text-muted">
                            {reservedAt}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  variant="ghost"
                  className="text-wt-text-secondary"
                  onClick={() => setBlockedOrdersDialogOpen(false)}
                  data-testid="blocked-orders-close-btn"
                >
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Boxes className="h-10 w-10 text-wt-text-muted mx-auto mb-2" />
              <p className="text-wt-text-muted">No active reservations blocking this SKU.</p>
              <Button
                variant="ghost"
                className="text-wt-text-secondary mt-3"
                onClick={() => setBlockedOrdersDialogOpen(false)}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryMasterPage;
